import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import AuthService from "@/services/AuthService";

// ── Types ─────────────────────────────────────────────────────────────────

interface ModelStat {
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
}

interface SiteStat {
  siteId: number;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  models: string[];
}

interface MonthlyTotal {
  year: number;
  month: number;
  calls: number;
  estimatedCostUsd: number;
}

interface SiteOverride {
  siteId: number;
  value: string;
  updatedBy: string;
  updatedAt: string;
}

interface FeedbackItem {
  id: number;
  siteId: number;
  feedbackText: string;
  triggerSource: string;
  sentimentScore: number | null;
  userEmail: string;
  pageContext: string;
  createdAt: string;
}

interface ActionLogItem {
  id: number;
  toolName: string;
  summary: string;
  triggeredBy: string;
  isRolledBack: boolean;
  rolledBackAt: string | null;
  rolledBackBy: string | null;
  createdAt: string;
}

interface AdminStats {
  days: number;
  activeModel: string;
  totalCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalEstimatedCostUsd: number;
  thisMonthCalls: number;
  thisMonthEstimatedCostUsd: number;
  feedbackCount: number;
  frustrationCount: number;
  byModel: ModelStat[];
  bySite: SiteStat[];
  monthlyTotals: MonthlyTotal[];
  siteOverrides: SiteOverride[];
  recentFeedback: FeedbackItem[];
  knowledgeChunks: number;
  knowledgeFiles: number;
}

// ── Model options ─────────────────────────────────────────────────────────

const MODEL_OPTIONS = [
  { id: "claude-haiku-4-5-20251001", label: "Haiku — cheapest", inputPer1k: "$0.0008", outputPer1k: "$0.004" },
  { id: "claude-sonnet-4-6",          label: "Sonnet — balanced", inputPer1k: "$0.003",  outputPer1k: "$0.015" },
  { id: "claude-opus-4-6",            label: "Opus — most capable", inputPer1k: "$0.015", outputPer1k: "$0.075" },
];

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ── Model cost reference (per 1M tokens in USD) ───────────────────────────

const MODEL_COSTS: Record<string, { input: number; output: number }> = {
  "claude-haiku-4-5-20251001": { input: 0.80,  output: 4.00  },
  "velaro-gpt-5-mini":         { input: 0.40,  output: 1.60  },
  "velaro-gpt-5":              { input: 2.50,  output: 10.00 },
  "velaro-gpt-4o":             { input: 2.50,  output: 10.00 },
  "velaro-gpt-4o-mini":        { input: 0.15,  output: 0.60  },
  "claude-sonnet-4-6":         { input: 3.00,  output: 15.00 },
  "claude-opus-4-6":           { input: 15.00, output: 75.00 },
};

const costLabel = (alias: string) => {
  const c = MODEL_COSTS[alias];
  if (!c) return null;
  const avg = (c.input + c.output) / 2;
  return avg < 1 ? "¢" : avg < 3 ? "$$" : avg < 10 ? "$$$" : "$$$$";
};

const costTooltip = (alias: string) => {
  const c = MODEL_COSTS[alias];
  if (!c) return "";
  return `$${c.input}/1M in · $${c.output}/1M out`;
};

// ── AI Role definitions ───────────────────────────────────────────────────

const AI_ROLES = [
  { role: "chatbot",               label: "Workflow Chatbot",          desc: "AI model for workflow/bot conversations — configure via Customer Chatbot section above (default: GPT-5 mini)" },
  { role: "sentiment",             label: "Sentiment Analysis",        desc: "Classifies conversation mood (positive/negative/neutral)" },
  { role: "theme_classification",  label: "Theme Classification",      desc: "Groups conversations by topic for reporting" },
  { role: "ticket_triage",         label: "Ticket Triage",             desc: "AI-suggested priority and routing for support tickets" },
  { role: "email_resolution",      label: "Email Resolution",          desc: "Drafts email replies in the inbox" },
  { role: "agent_assist",          label: "Agent Assist",              desc: "Real-time suggestions shown to human agents" },
  { role: "copilot",               label: "Admin Copilot (? bubble)",  desc: "The Velaro AI assistant for admin users" },
  { role: "report_narrative",      label: "Report Narrative",          desc: "Writes AI summaries and insights in reports" },
  { role: "intelligence_analysis", label: "Intelligence Analysis",     desc: "Deep conversation analysis and insights suite" },
  { role: "intelligence_suite",    label: "Intelligence Suite",        desc: "Full analytics suite AI processing" },
];

// ── Helpers ───────────────────────────────────────────────────────────────

const formatCost = (usd: number) =>
  usd === 0 ? "$0.00" : usd < 0.01 ? `<$0.01` : `$${usd.toFixed(2)}`;

const formatCostExact = (usd: number) =>
  usd === 0 ? "$0.000000" : `$${usd.toFixed(6)}`;

const formatTokens = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` :
  n >= 1_000     ? `${(n / 1_000).toFixed(1)}K` : `${n}`;

const modelShort = (m: string) =>
  m.includes("haiku")     ? "Haiku"   :
  m.includes("sonnet")    ? "Sonnet"  :
  m.includes("opus")      ? "Opus"    :
  m.includes("gpt-5-mini")? "GPT-5 mini" :
  m.includes("gpt-5")     ? "GPT-5"   :
  m.includes("gpt-4o-mini")? "GPT-4o mini":
  m.includes("gpt-4o")    ? "GPT-4o"  : m;

const triggerLabel = (t: string) =>
  t === "frustration_detected" ? "Auto — frustration" :
  t === "sentiment_api"        ? "Auto — sentiment"  : "User submitted";

// ── AddRoleOverride helper component ─────────────────────────────────────

function AddRoleOverride({
  siteId,
  availableModels,
  onSaved,
  siteIdInput = false,
}: {
  siteId: string;
  availableModels: { alias: string; displayName: string; brandedName: string; provider: string }[];
  onSaved: (role: string, model: string) => void;
  siteIdInput?: boolean;
}) {
  const api = useApi();
  const [selectedRole, setSelectedRole] = useState(AI_ROLES[0].role);
  const [selectedModel, setSelectedModel] = useState(availableModels[0]?.alias ?? "");
  const [inputSiteId, setInputSiteId] = useState(siteId !== "0" ? siteId : "");
  const [saving, setSaving] = useState(false);

  const effectiveSiteId = siteIdInput ? inputSiteId : siteId;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {siteIdInput && (
        <input
          type="text"
          value={inputSiteId}
          onChange={(e) => setInputSiteId(e.target.value)}
          placeholder="Site ID"
          className="w-24 bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5 font-mono"
        />
      )}
      <select
        value={selectedRole}
        onChange={(e) => setSelectedRole(e.target.value)}
        className="flex-1 bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5"
      >
        {AI_ROLES.map((r) => (
          <option key={r.role} value={r.role}>{r.label}</option>
        ))}
      </select>
      <select
        value={selectedModel}
        onChange={(e) => setSelectedModel(e.target.value)}
        className="flex-1 bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5"
      >
        {availableModels.map((m) => (
          <option key={m.alias} value={m.alias}>
            {m.brandedName || m.displayName} ({m.provider})
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!selectedModel || saving || !effectiveSiteId || effectiveSiteId === "0"}
        onClick={() => {
          setSaving(true);
          api.admin.post(`Copilot/SiteModelOverride/${effectiveSiteId}`, { role: selectedRole, model: selectedModel })
            .then(() => onSaved(selectedRole, selectedModel))
            .catch(() => alert("Save failed."))
            .finally(() => setSaving(false));
        }}
        className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-40 flex-shrink-0"
      >
        {saving ? "…" : "Add"}
      </button>
    </div>
  );
}

// ── ModelPicker — clickable card grid for selecting an AI model ──────────

const PROVIDER_COLORS: Record<string, string> = {
  Anthropic: "bg-orange-900/50 text-orange-300 border-orange-700/50",
  OpenAI:    "bg-green-900/50 text-green-300 border-green-700/50",
  Google:    "bg-blue-900/50 text-blue-300 border-blue-700/50",
  Alibaba:   "bg-yellow-900/50 text-yellow-300 border-yellow-700/50",
};

function ModelPicker({
  value, onChange, models, noneLabel = "(inherit global fallback)",
}: {
  value: string;
  onChange: (v: string) => void;
  models: { alias: string; displayName: string; provider: string; tier: string }[];
  noneLabel?: string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {noneLabel && (
        <button
          type="button"
          onClick={() => onChange("")}
          className={`flex flex-col items-start px-3 py-2 rounded-lg border text-left text-xs transition-colors ${
            value === ""
              ? "border-slate-400 bg-slate-700 text-white"
              : "border-slate-600 bg-slate-900 text-slate-400 hover:border-slate-500"
          }`}
        >
          <span className="font-medium italic">{noneLabel}</span>
        </button>
      )}
      {models.map((m) => {
        const selected = value === m.alias;
        const providerCls = PROVIDER_COLORS[m.provider] ?? "bg-slate-700 text-slate-300 border-slate-600";
        return (
          <button
            key={m.alias}
            type="button"
            onClick={() => onChange(m.alias)}
            className={`flex flex-col items-start px-3 py-2 rounded-lg border text-left transition-colors ${
              selected
                ? "border-emerald-500 bg-emerald-900/30 text-white ring-1 ring-emerald-500"
                : "border-slate-600 bg-slate-900 text-slate-300 hover:border-slate-400 hover:bg-slate-800"
            }`}
          >
            <span className="text-xs font-semibold">{m.displayName}</span>
            <span className={`mt-1 text-[10px] px-1.5 py-0.5 rounded border font-medium ${providerCls}`}>{m.provider}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────

export default function CopilotAdmin() {
  const api = useApi();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState<"usage"|"sites"|"feedback"|"knowledge"|"elevenlabs"|"actions"|"platform">("usage");

  // Platform AI Settings
  const [platformSettings, setPlatformSettings] = useState<{
    systemPromptHeader: string;
    modelBasic: string; modelPro: string; modelEnterprise: string;
    chatbotModelDefault: string; chatbotModelBasic: string; chatbotModelPro: string; chatbotModelEnterprise: string;
    isSystemPromptCustomized: boolean;
    roleDefaults: Record<string, string>;
    roleCodeDefaults: Record<string, string>;
    availableModels: { alias: string; displayName: string; brandedName: string; provider: string; tier: string; apiModelId: string; requiresProvisioning?: boolean; isInternal?: boolean }[];
  } | null>(null);
  const [chatbotModelDefault, setChatbotModelDefault] = useState("");
  const [chatbotModelBasic, setChatbotModelBasic] = useState("");
  const [chatbotModelPro, setChatbotModelPro] = useState("");
  const [chatbotModelEnterprise, setChatbotModelEnterprise] = useState("");
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformSaving, setPlatformSaving] = useState(false);
  const [platformDirty, setPlatformDirty] = useState(false);

  // Per-site model override
  const [siteOverrideSiteId, setSiteOverrideSiteId] = useState("");
  const [siteOverrideData, setSiteOverrideData] = useState<{
    subscriptionDefaultModel: string | null;
    roleOverrides: Record<string, string>;
  } | null>(null);
  const [siteOverrideLoading, setSiteOverrideLoading] = useState(false);
  const [siteOverrideSaving, setSiteOverrideSaving] = useState<string | null>(null);
  const [siteOverrideDefaultModel, setSiteOverrideDefaultModel] = useState("");
  const [providerSaving, setProviderSaving] = useState(false);
  const [siteUsage, setSiteUsage] = useState<Array<{role: string; modelId: string; inputTokens: number; outputTokens: number}> | null>(null);

  // Per-site effective models (what actually fires for each AI role)
  const [effectiveModels, setEffectiveModels] = useState<Array<{ role: string; model: string; isOverridden: boolean; codeDefault: string }> | null>(null);

  // All customer overrides (platform tab — management view)
  const [allSiteOverrides, setAllSiteOverrides] = useState<Array<{ siteId: number; role: string; model: string; updatedBy: string; updatedAt: string }> | null>(null);
  const [allOverridesLoading, setAllOverridesLoading] = useState(false);
  const [overrideEditSiteId, setOverrideEditSiteId] = useState<string | null>(null);
  const [overrideEditRole, setOverrideEditRole] = useState<string | null>(null);
  const [overrideEditModel, setOverrideEditModel] = useState("");
  const [overrideEditSaving, setOverrideEditSaving] = useState(false);
  const [overrideFilterRole, setOverrideFilterRole] = useState("");

  // Provider API Keys (Qwen, Google — superadmin sets these to enable those models)
  const [providerKeys, setProviderKeys] = useState<Array<{ provider: string; configured: boolean; maskedValue: string | null; updatedBy: string | null }> | null>(null);
  const [providerKeyInput, setProviderKeyInput] = useState<Record<string, string>>({});
  const [providerKeySaving, setProviderKeySaving] = useState<string | null>(null);

  // DB Alias Overrides (model routing without code deploy)
  const [aliasOverrides, setAliasOverrides] = useState<Array<{ from: string; to: string; updatedBy: string | null; updatedAt: string | null }> | null>(null);
  const [aliasOverridesLoading, setAliasOverridesLoading] = useState(false);
  const [newAliasFrom, setNewAliasFrom] = useState("");
  const [newAliasTo, setNewAliasTo] = useState("");
  const [aliasSaving, setAliasSaving] = useState(false);
  const [aliasDeleting, setAliasDeleting] = useState<string | null>(null);

  // Custom OpenAI-compatible providers
  const [customProviders, setCustomProviders] = useState<Array<{ alias: string; displayName: string; endpoint: string; modelId: string; providerKeyName: string | null; tier: string | null; notes: string | null; updatedBy: string | null; updatedAt: string | null }> | null>(null);
  const [customProvidersLoading, setCustomProvidersLoading] = useState(false);
  const [newCustomProvider, setNewCustomProvider] = useState({ alias: "", displayName: "", endpoint: "", modelId: "", providerKeyName: "", tier: "", notes: "" });
  const [customProviderSaving, setCustomProviderSaving] = useState(false);
  const [customProviderDeleting, setCustomProviderDeleting] = useState<string | null>(null);

  // Per-site AI credentials (customer's own key/endpoint)
  const [siteCredentials, setSiteCredentials] = useState<{ hasApiKeyOverride: boolean; endpointOverride: string | null } | null>(null);
  const [newApiKey, setNewApiKey] = useState("");
  const [newEndpoint, setNewEndpoint] = useState("");
  const [credentialsSaving, setCredentialsSaving] = useState(false);
  const [credentialsSaved, setCredentialsSaved] = useState(false);
  const [credentialsClearing, setCredentialsClearing] = useState(false);

  // Role defaults dirty tracking (alias → current selection)
  const [roleOverrides, setRoleOverrides] = useState<Record<string, string>>({});

  // Action logs
  const [actionLogs, setActionLogs] = useState<ActionLogItem[]>([]);
  const [actionLogsLoading, setActionLogsLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState<number | null>(null);
  const [rollbackMsg, setRollbackMsg] = useState<string | null>(null);

  // Global model
  const [selectedModel, setSelectedModel] = useState("");
  const [savingModel, setSavingModel] = useState(false);
  const [modelSaved, setModelSaved] = useState(false);

  // Site override state
  const [overrideSiteId, setOverrideSiteId] = useState("");
  const [overrideModel, setOverrideModel] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);
  const [overrideSaved, setOverrideSaved] = useState<string | null>(null);

  // KB toggle
  const [kbEnabled, setKbEnabled] = useState(false);
  const [kbToggleSaving, setKbToggleSaving] = useState(false);

  // Ingest
  const [ingesting, setIngesting] = useState(false);
  const [ingestResult, setIngestResult] = useState<string | null>(null);

  // KB one-time reindex
  const [reindexing, setReindexing] = useState(false);
  const [reindexResult, setReindexResult] = useState<string | null>(null);
  const [vectorBackfilling, setVectorBackfilling] = useState(false);
  const [vectorResult, setVectorResult] = useState<string | null>(null);
  const [relinkTenantId, setRelinkTenantId] = useState("");
  const [relinking, setRelinking] = useState(false);
  const [relinkResult, setRelinkResult] = useState<string | null>(null);

  // ElevenLabs
  const [elStats, setElStats] = useState<{ totalCharacters: number; totalEstimatedCostUsd: number; bySite: { siteId: number; characters: number; estimatedCostUsd: number }[] } | null>(null);
  const [elLoading, setElLoading] = useState(false);

  useEffect(() => { loadStats(); }, [days]);

  useEffect(() => {
    if (activeTab !== "elevenlabs" || elStats) return;
    setElLoading(true);
    api.admin.get(`ElevenLabs/AdminStats?days=${days}`)
      .then((r) => r.json())
      .then((d) => setElStats(d))
      .catch(() => {})
      .finally(() => setElLoading(false));
  }, [activeTab, days]);

  useEffect(() => {
    // Pre-fill site 1032 (Velaro internal) in the per-site override panel when switching to platform tab
    if (activeTab === "platform" && !siteOverrideSiteId) {
      setSiteOverrideSiteId("1032");
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "platform" || allSiteOverrides !== null) return;
    setAllOverridesLoading(true);
    api.messaging.get("superadmin/ai-models/all-site-overrides")
      .then((r) => r.json())
      .then((d) => setAllSiteOverrides(Array.isArray(d) ? d : []))
      .catch(() => setAllSiteOverrides([]))
      .finally(() => setAllOverridesLoading(false));
  }, [activeTab, allSiteOverrides]);

  useEffect(() => {
    if (activeTab !== "platform" || providerKeys !== null) return;
    api.messaging.get("superadmin/ai-models/provider-keys")
      .then((r) => r.json())
      .then((d) => setProviderKeys(Array.isArray(d) ? d : []))
      .catch(() => setProviderKeys([]));
  }, [activeTab, providerKeys]);

  useEffect(() => {
    if (activeTab !== "platform" || platformSettings) return;
    setPlatformLoading(true);
    api.admin.get("Copilot/PlatformAISettings")
      .then((r) => r.json())
      .then((d) => {
        setPlatformSettings(d);
        setChatbotModelDefault(d.chatbotModelDefault ?? "");
        setChatbotModelBasic(d.chatbotModelBasic ?? "");
        setChatbotModelPro(d.chatbotModelPro ?? "");
        setChatbotModelEnterprise(d.chatbotModelEnterprise ?? "");
        // Initialize role overrides from loaded data (DB values first, fallback to code defaults)
        const initRoles: Record<string, string> = {};
        AI_ROLES.forEach(({ role }) => {
          initRoles[role] = d.roleDefaults?.[role] ?? d.roleCodeDefaults?.[role] ?? "";
        });
        setRoleOverrides(initRoles);
      })
      .catch(() => {})
      .finally(() => setPlatformLoading(false));
  }, [activeTab, platformSettings]);

  useEffect(() => {
    if (activeTab !== "platform" || aliasOverrides !== null) return;
    setAliasOverridesLoading(true);
    api.messaging.get("superadmin/ai-models/alias-overrides")
      .then((r) => r.json())
      .then((d) => setAliasOverrides(Array.isArray(d) ? d : []))
      .catch(() => setAliasOverrides([]))
      .finally(() => setAliasOverridesLoading(false));
  }, [activeTab, aliasOverrides]);

  useEffect(() => {
    if (activeTab !== "platform" || customProviders !== null) return;
    setCustomProvidersLoading(true);
    api.messaging.get("superadmin/ai-models/custom-providers")
      .then((r) => r.json())
      .then((d) => setCustomProviders(Array.isArray(d) ? d : []))
      .catch(() => setCustomProviders([]))
      .finally(() => setCustomProvidersLoading(false));
  }, [activeTab, customProviders]);

  useEffect(() => {
    if (activeTab !== "actions") return;
    setActionLogsLoading(true);
    api.admin.get("Copilot/Actions")
      .then((r) => r.json())
      .then((d) => setActionLogs(Array.isArray(d) ? d : []))
      .catch(() => setActionLogs([]))
      .finally(() => setActionLogsLoading(false));
  }, [activeTab]);

  const rollbackAction = async (id: number) => {
    setRollingBack(id); setRollbackMsg(null);
    try {
      const res = await api.admin.post(`Copilot/Actions/${id}/Rollback`, {});
      const data = await res.json();
      if (data.success) {
        setRollbackMsg(data.message ?? "Rolled back.");
        setActionLogs((prev) => prev.map((a) => a.id === id ? { ...a, isRolledBack: true } : a));
      } else {
        setRollbackMsg(data.error ?? "Rollback failed.");
      }
    } catch {
      setRollbackMsg("Rollback request failed.");
    } finally {
      setRollingBack(null);
      setTimeout(() => setRollbackMsg(null), 5000);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.get(`Copilot/AdminStats?days=${days}`);
      const data = await res.json();
      setStats(data);
      if (data.activeModel) setSelectedModel(data.activeModel);
      // Load KB toggle
      try {
        const sRes = await api.admin.get("Copilot/Settings");
        if (sRes.ok) {
          const arr: Array<{ Key?: string; key?: string; Value?: string; value?: string }> = await sRes.json();
          const kb = arr.find((s) => (s.Key ?? s.key) === "enableKbIngestion");
          setKbEnabled((kb?.Value ?? kb?.value) === "true");
        }
      } catch { /* non-fatal */ }
    } catch {
      setError("Could not load Copilot stats. You may not have admin access.");
    } finally {
      setLoading(false);
    }
  };

  const saveGlobalModel = async () => {
    setSavingModel(true); setModelSaved(false);
    try {
      await api.admin.post("Copilot/Settings", { key: "model", value: selectedModel, siteId: 0 });
      setModelSaved(true);
      setTimeout(() => setModelSaved(false), 3000);
      loadStats();
    } catch { setError("Could not save model."); }
    finally { setSavingModel(false); }
  };

  const saveSiteOverride = async () => {
    const sid = parseInt(overrideSiteId, 10);
    if (!sid || !overrideModel) return;
    setSavingOverride(true);
    try {
      await api.admin.post("Copilot/Settings", { key: "model", value: overrideModel, siteId: sid });
      setOverrideSaved(`Site ${sid} → ${modelShort(overrideModel)}`);
      setTimeout(() => setOverrideSaved(null), 4000);
      setOverrideSiteId(""); setOverrideModel("");
      loadStats();
    } catch { setError("Could not save site override."); }
    finally { setSavingOverride(false); }
  };

  const removeSiteOverride = async (siteId: number) => {
    try {
      await api.admin.post("Copilot/Settings", { key: "model", value: "", siteId });
      loadStats();
    } catch { setError("Could not remove override."); }
  };

  const triggerIngest = async () => {
    setIngesting(true); setIngestResult(null);
    try {
      const res = await api.admin.post("Copilot/Ingest", {});
      const data = await res.json();
      setIngestResult(data.message ?? `${data.chunks} chunks (${data.mdChunks ?? 0} docs + ${data.kbArticles ?? 0} KB articles). Ingest running in background — refresh in ~30s.`);
      loadStats();
    } catch { setIngestResult("Ingest trigger failed."); }
    finally { setIngesting(false); }
  };

  const checkIngestStatus = async () => {
    setIngesting(true); setIngestResult(null);
    try {
      const res = await api.admin.get("Copilot/Ingest/Status");
      const data = await res.json();
      setIngestResult(data.message ?? `${data.chunks} chunks indexed.`);
    } catch { setIngestResult("Status check failed."); }
    finally { setIngesting(false); }
  };

  // Monthly chart helpers
  const maxMonthCost = Math.max(1, ...(stats?.monthlyTotals ?? []).map(m => m.estimatedCostUsd));

  // ── Velaro-internal only ─────────────────────────────────────────────────
  // This page is never shown to customers. The nav hides it for non-Velaro
  // accounts, and this guard prevents direct URL access.
  if (AuthService.profile?.siteId !== 1032) return <Navigate to="/" replace />;

  if (loading) return <div className="p-8 text-slate-400 text-center">Loading AI Platform stats...</div>;
  if (error) return <div className="p-8"><div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-400">{error}</div></div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-white">AI Platform</h1>
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-900/50 text-red-300 border border-red-700/50 uppercase tracking-wide">
              Velaro Internal Only
            </span>
          </div>
          <p className="text-slate-400 text-sm">Model config · Per-site credentials · Cost tracking · Feedback · Knowledge base</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-slate-400 text-sm">Period:</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="bg-slate-800 border border-slate-600 text-white rounded px-2 py-1 text-sm">
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>
      </div>

      {/* This-month banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "This month — calls", value: (stats?.thisMonthCalls ?? 0).toLocaleString(), highlight: false },
          { label: "This month — est. cost", value: formatCost(stats?.thisMonthEstimatedCostUsd ?? 0), highlight: true },
          { label: `${days}d calls`, value: (stats?.totalCalls ?? 0).toLocaleString(), highlight: false },
          { label: `${days}d est. cost`, value: formatCost(stats?.totalEstimatedCostUsd ?? 0), highlight: true },
        ].map((s) => (
          <div key={s.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4 text-center">
            <div className={`text-2xl font-bold ${s.highlight ? "text-amber-400" : "text-white"}`}>{s.value}</div>
            <div className="text-slate-400 text-xs mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Model Config */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h2 className="text-white font-semibold mb-1">Admin Copilot Model (? bubble)</h2>
        <p className="text-slate-400 text-sm mb-4">
          Controls the model used by the <strong className="text-white">Moshky admin copilot</strong> (the ? bubble) — NOT customer-facing chatbots.
          To set customer chatbot models per plan tier or per site, use the <button className="text-blue-400 hover:text-blue-300 underline" onClick={() => setActiveTab("platform")}>Platform tab</button>.
        </p>

        {/* Global model */}
        <div className="mb-5">
          <div className="text-slate-300 text-xs font-semibold uppercase tracking-wide mb-2">Copilot Model</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {MODEL_OPTIONS.map((m) => (
              <button key={m.id}
                onClick={() => setSelectedModel(m.id)}
                className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                  selectedModel === m.id
                    ? "border-blue-500 bg-blue-900/30 text-white"
                    : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white"
                }`}>
                <span className="font-medium">{m.label}</span>
                <span className="ml-2 text-xs opacity-60">in:{m.inputPer1k} out:{m.outputPer1k}/1K</span>
                {stats?.activeModel === m.id && (
                  <span className="ml-2 text-xs bg-blue-700 text-white rounded-full px-1.5">active</span>
                )}
              </button>
            ))}
          </div>
          <button onClick={saveGlobalModel}
            disabled={savingModel || !selectedModel}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded px-4 py-1.5 text-sm font-medium transition-colors">
            {savingModel ? "Saving..." : "Save Copilot Model"}
          </button>
          {modelSaved && <span className="ml-3 text-green-400 text-sm">Saved!</span>}
        </div>

        {/* Site overrides */}
        <div>
          <div className="text-slate-300 text-xs font-semibold uppercase tracking-wide mb-2">Site Overrides</div>
          {Array.isArray(stats?.siteOverrides) && stats.siteOverrides.length > 0 ? (
            <div className="mb-3 space-y-2">
              {stats.siteOverrides.map((o) => (
                <div key={o.siteId} className="flex items-center gap-3 bg-slate-900/60 rounded-lg px-4 py-2 border border-slate-700">
                  <span className="text-slate-400 text-sm">Site {o.siteId}</span>
                  <span className="text-white text-sm font-medium">{modelShort(o.value)}</span>
                  <span className="text-slate-500 text-xs">by {o.updatedBy}</span>
                  <button onClick={() => removeSiteOverride(o.siteId)}
                    className="ml-auto text-xs text-red-400 hover:text-red-300 transition-colors">
                    Remove (use global)
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm mb-3">No site overrides — all sites use the global default.</p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <input type="number" value={overrideSiteId}
              onChange={(e) => setOverrideSiteId(e.target.value)}
              placeholder="Site ID"
              className="w-28 bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
            <select value={overrideModel} onChange={(e) => setOverrideModel(e.target.value)}
              className="bg-slate-900 border border-slate-600 text-white rounded px-2 py-1.5 text-sm">
              <option value="">— pick model —</option>
              {MODEL_OPTIONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <button onClick={saveSiteOverride}
              disabled={savingOverride || !overrideSiteId || !overrideModel}
              className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded px-3 py-1.5 text-sm transition-colors">
              {savingOverride ? "Saving..." : "Set Override"}
            </button>
            {overrideSaved && <span className="text-green-400 text-sm">Saved: {overrideSaved}</span>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700">
        {(["usage","sites","feedback","knowledge","elevenlabs","actions","platform"] as const).map((t) => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
              activeTab === t ? "border-blue-500 text-white" : "border-transparent text-slate-400 hover:text-white"
            }`}>
            {t === "sites" ? "Per-Site Costs" : t === "knowledge" ? "Knowledge Base" : t === "elevenlabs" ? "ElevenLabs Voice" : t === "actions" ? "Action Log" : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab: Usage */}
      {activeTab === "usage" && (
        <div className="space-y-5">
          {/* Monthly bar chart */}
          {Array.isArray(stats?.monthlyTotals) && stats.monthlyTotals.length > 0 && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-4">Rolling 12-Month Cost</h3>
              <div className="flex items-end gap-2 h-32">
                {stats.monthlyTotals.map((m) => {
                  const pct = (m.estimatedCostUsd / maxMonthCost) * 100;
                  const isThisMonth = m.year === new Date().getFullYear() && m.month === new Date().getMonth() + 1;
                  return (
                    <div key={`${m.year}-${m.month}`} className="flex-1 flex flex-col items-center gap-1 group relative">
                      <div className="w-full rounded-t transition-all"
                        style={{
                          height: `${Math.max(4, pct)}%`,
                          backgroundColor: isThisMonth ? "#3b82f6" : "#374151",
                          minHeight: "4px"
                        }} />
                      <span className="text-slate-500 text-xs">{MONTH_NAMES[m.month - 1]}</span>
                      {/* Tooltip on hover */}
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
                        {MONTH_NAMES[m.month - 1]} {m.year}<br/>{m.calls} calls · {formatCost(m.estimatedCostUsd)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* By model */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <h3 className="text-white font-semibold mb-4">Usage by Model ({days} days)</h3>
            {Array.isArray(stats?.byModel) && stats.byModel.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-slate-400 text-xs">
                    <th className="text-left py-2">Model</th>
                    <th className="text-right py-2">Calls</th>
                    <th className="text-right py-2">Input</th>
                    <th className="text-right py-2">Output</th>
                    <th className="text-right py-2">Est. Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.byModel.map((row) => (
                    <tr key={row.model} className="border-b border-slate-800 text-slate-300">
                      <td className="py-2 font-mono text-xs">{row.model}</td>
                      <td className="py-2 text-right">{row.calls.toLocaleString()}</td>
                      <td className="py-2 text-right">{formatTokens(row.inputTokens)}</td>
                      <td className="py-2 text-right">{formatTokens(row.outputTokens)}</td>
                      <td className="py-2 text-right text-amber-400 font-mono text-xs">{formatCostExact(row.estimatedCostUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">No usage data in this period.</p>
            )}
          </div>
        </div>
      )}

      {/* Tab: Per-Site Costs */}
      {activeTab === "sites" && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-1">Cost per Tenant ({days} days)</h3>
          <p className="text-slate-400 text-sm mb-4">Sorted by estimated cost descending. Costs are estimates based on per-token pricing at time of each call.</p>
          {Array.isArray(stats?.bySite) && stats.bySite.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-slate-400 text-xs">
                  <th className="text-left py-2">Site ID</th>
                  <th className="text-right py-2">Calls</th>
                  <th className="text-right py-2">Input</th>
                  <th className="text-right py-2">Output</th>
                  <th className="text-right py-2">Est. Cost</th>
                  <th className="text-left py-2 pl-4">Model(s)</th>
                  <th className="text-right py-2">Override</th>
                </tr>
              </thead>
              <tbody>
                {stats.bySite.map((row) => {
                  const override = stats.siteOverrides?.find(o => o.siteId === row.siteId);
                  return (
                    <tr key={row.siteId} className="border-b border-slate-800 text-slate-300 hover:bg-slate-700/30">
                      <td className="py-2 font-mono text-xs">{row.siteId}</td>
                      <td className="py-2 text-right">{row.calls.toLocaleString()}</td>
                      <td className="py-2 text-right text-xs">{formatTokens(row.inputTokens)}</td>
                      <td className="py-2 text-right text-xs">{formatTokens(row.outputTokens)}</td>
                      <td className="py-2 text-right text-amber-400 font-mono text-xs">{formatCostExact(row.estimatedCostUsd)}</td>
                      <td className="py-2 pl-4 text-xs text-slate-400">{(row.models ?? []).map(modelShort).join(", ")}</td>
                      <td className="py-2 text-right">
                        {override ? (
                          <span className="text-blue-400 text-xs">{modelShort(override.value)}</span>
                        ) : (
                          <span className="text-slate-600 text-xs">global</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-slate-500 text-sm text-center py-8">No per-site data yet — usage will appear here after the first Copilot chats.</p>
          )}
        </div>
      )}

      {/* Tab: Feedback */}
      {activeTab === "feedback" && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Feedback Inbox ({days} days)</h3>
            <div className="flex gap-4 text-sm">
              <span className="text-slate-400">Total: <span className="text-white font-semibold">{stats?.feedbackCount ?? 0}</span></span>
              <span className="text-amber-400">Frustration triggered: <span className="font-semibold">{stats?.frustrationCount ?? 0}</span></span>
            </div>
          </div>
          {Array.isArray(stats?.recentFeedback) && stats.recentFeedback.length > 0 ? (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {stats.recentFeedback.map((fb) => (
                <div key={fb.id} className="bg-slate-900/60 rounded-lg p-4 border border-slate-700">
                  <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                    <div className="flex gap-2 items-center flex-wrap">
                      <span className="text-slate-500 text-xs font-mono">Site {fb.siteId}</span>
                      {fb.userEmail && <span className="text-slate-400 text-xs">{fb.userEmail}</span>}
                      <span className={`text-xs rounded-full px-2 py-0.5 ${
                        fb.triggerSource === "frustration_detected"
                          ? "bg-amber-900/50 text-amber-400"
                          : "bg-blue-900/50 text-blue-400"
                      }`}>{triggerLabel(fb.triggerSource)}</span>
                      {fb.pageContext && <span className="text-slate-500 text-xs">{fb.pageContext}</span>}
                    </div>
                    <span className="text-slate-500 text-xs whitespace-nowrap">{new Date(fb.createdAt).toLocaleDateString()}</span>
                  </div>
                  <p className="text-slate-200 text-sm">{fb.feedbackText}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm text-center py-8">No feedback in this period.</p>
          )}
        </div>
      )}

      {/* Tab: ElevenLabs Voice */}
      {activeTab === "elevenlabs" && (
        <div className="space-y-5">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: `${days}d characters`, value: (elStats?.totalCharacters ?? 0).toLocaleString() },
              { label: `${days}d est. cost`, value: formatCost(elStats?.totalEstimatedCostUsd ?? 0), highlight: true },
              { label: "pricing", value: "$0.03/1K chars (Turbo)", note: true },
            ].map((s) => (
              <div key={s.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4 text-center">
                <div className={`text-2xl font-bold ${s.highlight ? "text-amber-400" : "text-white"}`}>{s.value}</div>
                <div className="text-slate-400 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {elLoading ? (
            <div className="text-slate-400 text-center py-8">Loading ElevenLabs stats...</div>
          ) : (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-4">Voice Usage by Site ({days} days)</h3>
              {Array.isArray(elStats?.bySite) && elStats.bySite.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700 text-slate-400 text-xs">
                      <th className="text-left py-2">Site ID</th>
                      <th className="text-right py-2">Characters</th>
                      <th className="text-right py-2">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {elStats.bySite.map((row) => (
                      <tr key={row.siteId} className="border-b border-slate-800 text-slate-300 hover:bg-slate-700/30">
                        <td className="py-2 font-mono text-xs">{row.siteId}</td>
                        <td className="py-2 text-right">{row.characters.toLocaleString()}</td>
                        <td className="py-2 text-right text-amber-400 font-mono text-xs">{formatCostExact(row.estimatedCostUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-slate-500 text-sm text-center py-8">
                  No voice usage yet. Enable voice on an AI bot config to start generating TTS usage.
                </p>
              )}
            </div>
          )}

          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 text-sm text-slate-400 space-y-2">
            <div className="text-white font-medium">ElevenLabs Pricing Reference</div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[
                { model: "eleven_turbo_v2_5",      label: "Turbo v2.5",      price: "$0.030/1K" },
                { model: "eleven_flash_v2_5",      label: "Flash v2.5",      price: "$0.015/1K" },
                { model: "eleven_multilingual_v2", label: "Multilingual v2", price: "$0.030/1K" },
              ].map((m) => (
                <div key={m.model} className="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
                  <div className="text-white text-xs font-medium">{m.label}</div>
                  <div className="text-amber-400 text-xs font-mono mt-1">{m.price} chars</div>
                  <div className="text-slate-500 text-xs mt-1 font-mono">{m.model}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Enable voice per bot in AI Bot Settings → General → Voice Settings.
              Gate by site: toggle <code className="text-green-400 bg-slate-800 px-1 rounded text-xs">EnableBotVoice</code> in Database Tool.
            </p>
          </div>
        </div>
      )}

      {/* Tab: Knowledge Base */}
      {activeTab === "knowledge" && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-5">
          <div>
            <h3 className="text-white font-semibold mb-4">Knowledge Base Status</h3>
            <div className="flex gap-8 mb-4">
              <div>
                <div className="text-3xl font-bold text-white">{stats?.knowledgeChunks ?? 0}</div>
                <div className="text-slate-400 text-sm">chunks indexed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">{stats?.knowledgeFiles ?? 0}</div>
                <div className="text-slate-400 text-sm">source files</div>
              </div>
            </div>
          </div>

          <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4 text-sm space-y-2">
            <div className="text-white font-medium">Where to put knowledge files</div>
            <p className="text-slate-400">
              Drop any <code className="text-green-400 bg-slate-800 px-1 py-0.5 rounded text-xs">.md</code> file into the{" "}
              <code className="text-green-400 bg-slate-800 px-1 py-0.5 rounded text-xs">copilot-kb/</code> folder in the repo root{" "}
              (<code className="text-slate-400 bg-slate-800 px-1 py-0.5 rounded text-xs">C:\Users\alex\velaro-admin\copilot-kb\</code>).
              The ingest service picks it up on next app startup or within 24 hours. No code change needed.
            </p>
            <p className="text-slate-400">
              Supplementary docs also scanned from{" "}
              <code className="text-green-400 bg-slate-800 px-1 py-0.5 rounded text-xs">docs/</code> — for files like the variables guide.
              Do <strong className="text-white">not</strong> put internal planning docs (todos, architecture notes) in either folder — they will be served to customers.
            </p>
            <p className="text-slate-400">
              <strong className="text-white">Ingest frequency:</strong> on app startup (15s delay) + every 24 hours automatically. Content changes are detected by comparison — unchanged files are skipped.
            </p>
            <p className="text-slate-400">
              <strong className="text-white">How it works:</strong> each <code className="text-green-400 bg-slate-800 px-1 rounded text-xs">##</code> heading becomes one chunk (max ~1,500 chars). Keywords are extracted and stored. When a customer asks a question, the top 6 matching chunks are injected into the Claude system prompt as "RELEVANT DOCUMENTATION". Claude answers only from that content.
            </p>
          </div>

          {/* KB article ingestion toggle */}
          <div className="border border-slate-700 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-white text-sm font-medium mb-1">v20 KB Article Ingestion</div>
                <p className="text-slate-400 text-xs">
                  When enabled, published v20 Knowledge Base articles will be ingested alongside copilot-kb/ and docs/.
                </p>
                <p className="text-amber-500 text-xs mt-2">
                  Pending: KB article source filter not yet implemented. Enable once v20-specific articles are published.
                </p>
              </div>
              <button
                onClick={async () => {
                  const next = !kbEnabled;
                  setKbToggleSaving(true);
                  try {
                    await api.admin.post("Copilot/Settings", { key: "enableKbIngestion", value: next ? "true" : "false", siteId: 0 });
                    setKbEnabled(next);
                  } catch { /* non-fatal */ } finally { setKbToggleSaving(false); }
                }}
                disabled={kbToggleSaving}
                className={`flex-shrink-0 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  kbEnabled ? "bg-green-700 hover:bg-green-800 text-white" : "bg-slate-700 hover:bg-slate-600 text-slate-300"
                }`}>
                {kbToggleSaving ? "Saving..." : kbEnabled ? "Enabled" : "Disabled"}
              </button>
            </div>
          </div>

          {/* Trigger + status buttons */}
          <div className="flex gap-2 flex-wrap">
            <button onClick={triggerIngest} disabled={ingesting}
              className="bg-blue-700 hover:bg-blue-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded px-4 py-2 text-sm transition-colors">
              {ingesting ? "Triggering..." : "Force Re-ingest Now"}
            </button>
            <button onClick={checkIngestStatus} disabled={ingesting}
              className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded px-4 py-2 text-sm transition-colors">
              Check Status
            </button>
          </div>
          <p className="text-slate-500 text-xs">
            "Force Re-ingest Now" rescans copilot-kb/ .md files + all published KB articles with Bot Response enabled, and updates the SQL chunk table immediately (runs in background ~30s). This is the same pipeline the copilot queries. Runs automatically on startup + every 24h.
          </p>
          {ingestResult && (
            <div className="text-sm text-slate-300 bg-slate-900/60 rounded p-3 border border-slate-700">{ingestResult}</div>
          )}

          {/* ── KB tenant relink (for migrated tenants) ───────────────────── */}
          <div className="border border-slate-700 rounded-lg p-4 space-y-3">
            <div className="text-white text-sm font-semibold">Force-Relink KB Tenant</div>
            <p className="text-slate-400 text-xs">
              If KB articles were migrated from V10 and the admin UI shows nothing, the tenant ID isn't linked to this site in SiteConfiguration. Enter the tenantId from the KBTenants table to link it. After relinking, hit "Force Re-ingest Now" above.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-xs text-white font-mono placeholder-slate-500 focus:outline-none focus:border-blue-500"
                placeholder="tenant-id (e.g. a1b2c3d4-...)"
                value={relinkTenantId}
                onChange={(e) => setRelinkTenantId(e.currentTarget.value)}
              />
              <button
                disabled={relinking || !relinkTenantId.trim()}
                onClick={async () => {
                  setRelinking(true); setRelinkResult(null);
                  try {
                    const res = await api.admin.put("api/kb/tenants/relink", { tenantId: relinkTenantId.trim() });
                    const data = await res.json().catch(() => ({}));
                    if (res.ok) {
                      setRelinkResult(`✓ Linked: ${(data as any).CompanyName ?? relinkTenantId} — now hit Force Re-ingest`);
                    } else {
                      setRelinkResult(`✗ ${(data as any).error ?? "Relink failed"}`);
                    }
                  } catch { setRelinkResult("✗ Network error"); }
                  finally { setRelinking(false); }
                }}
                className="bg-blue-700 hover:bg-blue-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded px-3 py-1.5 text-xs transition-colors whitespace-nowrap"
              >
                {relinking ? "Linking..." : "Relink"}
              </button>
            </div>
            {relinkResult && (
              <div className={`text-xs rounded px-2 py-1.5 ${relinkResult.startsWith("✓") ? "text-green-400 bg-green-900/20" : "text-red-400 bg-red-900/20"}`}>{relinkResult}</div>
            )}
          </div>

          {/* ── Customer-facing KB search index (separate from copilot) ──── */}
          <div className="border border-slate-700 rounded-lg p-4 space-y-3">
            <div className="text-white text-sm font-semibold">Customer-Facing KB Search Index</div>
            <p className="text-slate-400 text-xs">
              Separate from the copilot pipeline above. This pushes articles into <strong className="text-white">Azure AI Search</strong> for the public <code className="text-green-400 bg-slate-800 px-1 rounded">help.velaro.com</code> search bar and the bot's answer index (Ada-002 vector embeddings). Requires Azure Search + Azure OpenAI to be configured.
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                disabled={reindexing}
                onClick={async () => {
                  setReindexing(true); setReindexResult(null);
                  try {
                    const res = await api.admin.post("api/kb/search/reindex", {});
                    const data = await res.json().catch(() => ({}));
                    setReindexResult((data as any).message ?? `${(data as any).count ?? 0} articles pushed to Azure Search.`);
                  } catch { setReindexResult("✗ Failed — check server logs"); }
                  finally { setReindexing(false); }
                }}
                className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-500 text-white rounded px-3 py-1.5 text-xs transition-colors"
              >
                {reindexing ? "Indexing..." : "Re-index (BM25 Search)"}
              </button>
              <button
                disabled={vectorBackfilling}
                onClick={async () => {
                  setVectorBackfilling(true); setVectorResult(null);
                  try {
                    const res = await api.admin.post("api/kb/search/vector-backfill", {});
                    const data = await res.json().catch(() => ({}));
                    setVectorResult((data as any).message ?? "Vector backfill queued.");
                  } catch { setVectorResult("✗ Failed — check server logs"); }
                  finally { setVectorBackfilling(false); }
                }}
                className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-500 text-white rounded px-3 py-1.5 text-xs transition-colors"
              >
                {vectorBackfilling ? "Queuing..." : "Backfill Vector/AI Index"}
              </button>
            </div>
            {reindexResult && <div className="text-xs text-slate-300 bg-slate-900/60 rounded px-2 py-1.5">{reindexResult}</div>}
            {vectorResult && <div className="text-xs text-slate-300 bg-slate-900/60 rounded px-2 py-1.5">{vectorResult}</div>}
          </div>
        </div>
      )}

      {/* ── Action Log tab ──────────────────────────────────────────────── */}
      {activeTab === "actions" && (
        <div className="space-y-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <h2 className="text-white font-semibold mb-1">Copilot Action Log</h2>
            <p className="text-slate-400 text-sm mb-4">
              Every configuration change made by the Velaro Copilot is recorded here with a before/after snapshot.
              You can roll back any change with one click — the system will revert to the exact state before the copilot acted.
            </p>

            {rollbackMsg && (
              <div className="mb-3 text-sm bg-blue-900/30 border border-blue-700 text-blue-300 rounded px-3 py-2">
                {rollbackMsg}
              </div>
            )}

            {actionLogsLoading ? (
              <div className="text-slate-400 text-sm py-6 text-center">Loading action log...</div>
            ) : actionLogs.length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center">
                No copilot actions recorded yet. When the copilot renames a deployment, assigns a team, or makes any other configuration change, it will appear here.
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto space-y-2">
                {actionLogs.map((log) => (
                  <div key={log.id}
                    className={`flex items-start justify-between gap-3 rounded-lg px-4 py-3 border ${
                      log.isRolledBack
                        ? "bg-slate-900/40 border-slate-700 opacity-60"
                        : "bg-slate-900 border-slate-600"
                    }`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono bg-slate-700 text-slate-300 rounded px-1.5 py-0.5">
                          {log.toolName}
                        </span>
                        {log.isRolledBack && (
                          <span className="text-xs bg-amber-900/40 border border-amber-700 text-amber-400 rounded px-1.5 py-0.5">
                            rolled back {log.rolledBackBy ? `by ${log.rolledBackBy}` : ""}
                          </span>
                        )}
                      </div>
                      <div className="text-white text-sm mt-1">{log.summary}</div>
                      <div className="text-slate-500 text-xs mt-1">
                        {new Date(log.createdAt).toLocaleString()} · by {log.triggeredBy || "copilot"}
                      </div>
                    </div>
                    {!log.isRolledBack && (
                      <button
                        onClick={() => rollbackAction(log.id)}
                        disabled={rollingBack === log.id}
                        className="shrink-0 text-xs bg-red-900/30 hover:bg-red-900/60 border border-red-700 text-red-400 hover:text-red-300 rounded px-3 py-1.5 transition-colors disabled:opacity-50"
                      >
                        {rollingBack === log.id ? "Reverting..." : "Roll Back"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <h2 className="text-white font-semibold mb-1">MCP Server — External Access</h2>
            <p className="text-slate-400 text-sm mb-3">
              Expose these tools to external agents via the Velaro MCP endpoint. Requires <span className="text-white font-medium">MCP</span> subscription feature.
            </p>
            <div className="bg-slate-900 rounded p-3 font-mono text-xs text-slate-300 space-y-1">
              <div><span className="text-slate-500">Endpoint:</span> POST /mcp/copilot</div>
              <div><span className="text-slate-500">Protocol:</span> JSON-RPC 2.0</div>
              <div><span className="text-slate-500">Info:</span> GET /mcp/copilot/info</div>
              <div><span className="text-slate-500">Auth:</span> Same session auth as the admin panel</div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Available tools: get_subscription_status · get_deployment_info · get_team_config · get_connected_integrations · get_recent_actions · rename_deployment · assign_team_to_deployment
            </div>
          </div>
        </div>
      )}

      {/* ── Platform AI Settings tab ─────────────────────────────────────── */}
      {activeTab === "platform" && (
        <div className="space-y-5">
          {platformLoading && (
            <div className="text-slate-400 text-sm">Loading platform settings…</div>
          )}
          {platformSettings && (
            <>
              {/* Header banner */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 border border-slate-600 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-white font-bold text-base mb-1">Platform AI Settings</h2>
                    <p className="text-slate-400 text-sm">
                      Controls defaults for <strong className="text-white">all customers</strong>.
                      Changes apply on next conversation — no deployment needed.
                    </p>
                  </div>
                  {platformSettings.isSystemPromptCustomized && (
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex-shrink-0">
                      Custom prompt active
                    </span>
                  )}
                </div>
              </div>

              {/* ── Section 1: AI Functions (per-role global defaults) ─────── */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-1">AI Functions</h3>
                <p className="text-slate-400 text-xs mb-4">
                  Set the model used for each AI function platform-wide. These are global defaults — any site
                  can have a per-site override (see below). A green dot means a DB override is active; gray means
                  the code default is in use.
                </p>
                <div className="space-y-2">
                  {AI_ROLES.map(({ role, label, desc }) => {
                    const dbValue   = platformSettings.roleDefaults?.[role];
                    const codeValue = platformSettings.roleCodeDefaults?.[role] ?? "";
                    const current   = roleOverrides[role] ?? codeValue;
                    const hasDbOverride = !!dbValue;
                    const nonInternalModels = (platformSettings.availableModels ?? []).filter((m) => !m.isInternal);
                    return (
                      <div key={role} className="flex items-center gap-3 py-2 border-b border-slate-700/50 last:border-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${hasDbOverride ? "bg-emerald-400" : "bg-slate-600"}`} title={hasDbOverride ? "DB override active" : "Using code default"} />
                            <span className="text-sm text-white font-medium">{label}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 ml-4">{desc}</p>
                        </div>
                        <select
                          value={current}
                          onChange={(e) => {
                            setRoleOverrides((prev) => ({ ...prev, [role]: e.target.value }));
                            setPlatformDirty(true);
                          }}
                          className="w-64 bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5 flex-shrink-0"
                        >
                          {nonInternalModels.map((m) => (
                            <option key={m.alias} value={m.alias}>
                              {m.brandedName || m.displayName} ({m.provider})
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Section: Customer Chatbot Default Model ───────────────── */}
              <div className="bg-slate-800 border border-emerald-600/40 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-1">Customer Chatbot Default Model</h3>
                <p className="text-slate-400 text-xs mb-4">
                  Controls which AI model customer-facing chatbots use when neither the bot persona nor the site have an explicit override.
                  Resolution order: <span className="text-slate-300">Persona model → Site default → Plan tier (below) → Global default (below) → code constant (GPT-5 mini)</span>.
                  Changes take effect within 5 minutes (cache TTL).
                </p>
                {/* Global fallback */}
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Global Fallback (all plans)</div>
                    <span className="text-xs font-mono bg-slate-700 text-emerald-400 px-1.5 py-0.5 rounded">writes model.chatbot</span>
                  </div>
                  <ModelPicker
                    value={chatbotModelDefault}
                    onChange={(v) => { setChatbotModelDefault(v); setPlatformDirty(true); }}
                    models={(platformSettings.availableModels ?? []).filter(m => !m.isInternal)}
                    noneLabel="(code constant — GPT-5 mini)"
                  />
                </div>
                {/* Per-plan overrides */}
                <div className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-3">Override by Plan</div>
                <div className="grid grid-cols-1 gap-4">
                  {([
                    { tier: "starter",      label: "Starter",      dbKey: "model_tier_starter",      val: chatbotModelBasic,      set: setChatbotModelBasic,      borderCls: "border-slate-600" },
                    { tier: "professional", label: "Professional",  dbKey: "model_tier_professional",  val: chatbotModelPro,        set: setChatbotModelPro,        borderCls: "border-blue-500/50" },
                    { tier: "enterprise",   label: "Enterprise",    dbKey: "model_tier_enterprise",    val: chatbotModelEnterprise, set: setChatbotModelEnterprise, borderCls: "border-purple-500/50" },
                  ] as const).map(({ tier, label, dbKey, val, set, borderCls }) => (
                    <div key={tier} className={`border rounded-lg p-3 ${borderCls}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</div>
                        <span className="text-xs font-mono bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded">{dbKey}</span>
                      </div>
                      <ModelPicker
                        value={val}
                        onChange={(v) => { set(v); setPlatformDirty(true); }}
                        models={(platformSettings.availableModels ?? []).filter(m => !m.isInternal)}
                        noneLabel="(inherit global fallback)"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Section 2: Default by Subscription Tier ───────────────── */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-1">Internal AI Model by Subscription Tier</h3>
                <p className="text-slate-400 text-xs mb-4">
                  Controls background AI tasks (sentiment, ticket triage, agent assist) — NOT customer chatbots.
                  Use the section above for chatbot defaults.
                  Model aliases like <code className="text-slate-300">velaro-gpt-4o-mini</code> map to the actual API model ID in
                  velaro-messaging's <code className="text-slate-300">ModelResolver.cs</code> — update the alias map there to retire a model globally.
                </p>
                <div className="grid grid-cols-1 gap-4">
                  {(["basic","pro","enterprise"] as const).map((tier) => {
                    const key = `model${tier.charAt(0).toUpperCase()}${tier.slice(1)}` as "modelBasic"|"modelPro"|"modelEnterprise";
                    const tierColors: Record<string, string> = {
                      basic: "border-slate-600", pro: "border-blue-500/50", enterprise: "border-purple-500/50"
                    };
                    const tierModels = (platformSettings.availableModels ?? []).filter((m) =>
                      !m.isInternal && (
                        tier === "enterprise" ? true :
                        tier === "pro"        ? m.tier !== "enterprise" :
                        m.tier === "basic"
                      )
                    );
                    return (
                      <div key={tier} className={`border rounded-lg p-3 ${tierColors[tier]}`}>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 capitalize">{tier}</div>
                        <ModelPicker
                          value={platformSettings[key]}
                          onChange={(v) => {
                            setPlatformSettings((s) => s ? { ...s, [key]: v } : s);
                            setPlatformDirty(true);
                          }}
                          models={tierModels}
                          noneLabel=""
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Section 3: Per-Site AI Override ───────────────────────── */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-1">Per-Site AI Override</h3>
                <p className="text-slate-400 text-xs mb-4">
                  Override models for a specific customer account. Overrides take precedence over all global defaults.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="number"
                    placeholder="Site ID"
                    value={siteOverrideSiteId}
                    onChange={(e) => setSiteOverrideSiteId(e.target.value)}
                    className="w-32 bg-slate-900 border border-slate-600 rounded text-white text-sm px-3 py-1.5"
                  />
                  <button
                    type="button"
                    disabled={!siteOverrideSiteId || siteOverrideLoading}
                    onClick={() => {
                      setSiteOverrideLoading(true);
                      setSiteOverrideData(null);
                      setSiteUsage(null);
                      setSiteCredentials(null);
                      setEffectiveModels(null);
                      setNewApiKey("");
                      setNewEndpoint("");
                      Promise.all([
                        api.admin.get(`Copilot/SiteModelOverride/${siteOverrideSiteId}`).then((r) => r.json()),
                        api.admin.get(`Copilot/SiteModelOverride/${siteOverrideSiteId}/usage`).then((r) => r.json()).catch(() => null),
                        api.admin.get(`Copilot/SiteAiCredentials/${siteOverrideSiteId}`).then((r) => r.json()).catch(() => null),
                        api.messaging.get(`superadmin/ai-models/site/${siteOverrideSiteId}/effective`).then((r) => r.json()).catch(() => null),
                      ])
                        .then(([d, usage, creds, effective]) => {
                          setSiteOverrideData(d);
                          setSiteOverrideDefaultModel(d.subscriptionDefaultModel ?? "");
                          setSiteUsage(Array.isArray(usage) ? usage : null);
                          setSiteCredentials(creds ?? null);
                          setNewEndpoint(creds?.endpointOverride ?? "");
                          setEffectiveModels(Array.isArray(effective) ? effective : null);
                        })
                        .catch(() => alert("Look-up failed."))
                        .finally(() => setSiteOverrideLoading(false));
                    }}
                    className="px-4 py-1.5 rounded bg-slate-600 hover:bg-slate-500 text-white text-sm font-medium disabled:opacity-40"
                  >
                    {siteOverrideLoading ? "Loading…" : "Look Up"}
                  </button>
                </div>

                {siteOverrideData && (
                  <div className="space-y-4">
                    {/* Site Default Model */}
                    <div className="border border-slate-700 rounded-lg p-4">
                      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Site Default Model</div>
                      <div className="flex items-center gap-3">
                        <select
                          value={siteOverrideDefaultModel}
                          onChange={(e) => setSiteOverrideDefaultModel(e.target.value)}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5"
                        >
                          <option value="">(platform default)</option>
                          {(platformSettings.availableModels ?? []).filter((m) => !m.isInternal).map((m) => (
                            <option key={m.alias} value={m.alias}>
                              {m.brandedName || m.displayName} ({m.provider})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={siteOverrideSaving === "default"}
                          onClick={() => {
                            setSiteOverrideSaving("default");
                            const payload = siteOverrideDefaultModel
                              ? { role: "default", model: siteOverrideDefaultModel }
                              : null;
                            const req = siteOverrideDefaultModel
                              ? api.admin.post(`Copilot/SiteModelOverride/${siteOverrideSiteId}`, payload)
                              : api.admin.delete(`Copilot/SiteModelOverride/${siteOverrideSiteId}/default`);
                            req
                              .then(() => setSiteOverrideData((prev) => prev ? { ...prev, subscriptionDefaultModel: siteOverrideDefaultModel || null } : prev))
                              .catch(() => alert("Save failed."))
                              .finally(() => setSiteOverrideSaving(null));
                          }}
                          className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-40"
                        >
                          {siteOverrideSaving === "default" ? "Saving…" : "Save"}
                        </button>
                        {siteOverrideData.subscriptionDefaultModel && (
                          <button
                            type="button"
                            disabled={siteOverrideSaving === "default"}
                            onClick={() => {
                              setSiteOverrideSaving("default");
                              api.admin.delete(`Copilot/SiteModelOverride/${siteOverrideSiteId}/default`)
                                .then(() => {
                                  setSiteOverrideData((prev) => prev ? { ...prev, subscriptionDefaultModel: null } : prev);
                                  setSiteOverrideDefaultModel("");
                                })
                                .catch(() => alert("Clear failed."))
                                .finally(() => setSiteOverrideSaving(null));
                            }}
                            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs disabled:opacity-40"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {!siteOverrideData.subscriptionDefaultModel && (
                        <p className="text-xs text-slate-500 mt-1">Using platform default</p>
                      )}
                    </div>

                    {/* Quick Provider Setup */}
                    <div className="border border-slate-700 rounded-lg p-4">
                      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Quick Provider Setup</div>
                      <p className="text-xs text-slate-500 mb-3">Bulk-set all AI roles to one provider's model lineup. Sets the site default + all 10 role overrides at once.</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { label: "All Anthropic", value: "anthropic", cls: "border-orange-500/50 text-orange-300 hover:bg-orange-900/20" },
                          { label: "All OpenAI",    value: "openai",    cls: "border-green-500/50 text-green-300 hover:bg-green-900/20" },
                          { label: "All Google",    value: "google",    cls: "border-blue-500/50 text-blue-300 hover:bg-blue-900/20" },
                          { label: "Reset to defaults", value: "reset", cls: "border-slate-600 text-slate-400 hover:bg-slate-700" },
                        ].map((btn) => (
                          <button
                            key={btn.value}
                            type="button"
                            disabled={providerSaving}
                            onClick={() => {
                              setProviderSaving(true);
                              api.admin.post(`Copilot/SiteModelOverride/${siteOverrideSiteId}/provider`, { provider: btn.value })
                                .then(() => {
                                  // Refresh both override data and usage after bulk update
                                  return Promise.all([
                                    api.admin.get(`Copilot/SiteModelOverride/${siteOverrideSiteId}`).then((r) => r.json()),
                                    api.admin.get(`Copilot/SiteModelOverride/${siteOverrideSiteId}/usage`).then((r) => r.json()).catch(() => null),
                                  ]);
                                })
                                .then(([d, usage]) => {
                                  setSiteOverrideData(d);
                                  setSiteOverrideDefaultModel(d.subscriptionDefaultModel ?? "");
                                  setSiteUsage(Array.isArray(usage) ? usage : null);
                                })
                                .catch(() => alert("Provider switch failed."))
                                .finally(() => setProviderSaving(false));
                            }}
                            className={`px-3 py-1.5 rounded border text-xs font-medium transition-colors disabled:opacity-40 ${btn.cls}`}
                          >
                            {providerSaving ? "…" : btn.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Per-role overrides */}
                    <div className="border border-slate-700 rounded-lg p-4">
                      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Per-Role Overrides</div>
                      {Object.keys(siteOverrideData.roleOverrides).length === 0 ? (
                        <p className="text-xs text-slate-500">No per-role overrides — all roles use global defaults.</p>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(siteOverrideData.roleOverrides).map(([role, model]) => {
                            const roleLabel = AI_ROLES.find((r) => r.role === role)?.label ?? role;
                            const modelInfo = (platformSettings.availableModels ?? []).find((m) => m.alias === model);
                            return (
                              <div key={role} className="flex items-center gap-3 py-1.5 border-b border-slate-700/50 last:border-0">
                                <span className="text-sm text-white flex-1">{roleLabel}</span>
                                <span className="text-xs text-slate-400">{modelInfo ? `${modelInfo.brandedName || modelInfo.displayName} (${modelInfo.provider})` : model}</span>
                                <button
                                  type="button"
                                  disabled={siteOverrideSaving === role}
                                  onClick={() => {
                                    setSiteOverrideSaving(role);
                                    api.admin.delete(`Copilot/SiteModelOverride/${siteOverrideSiteId}/${role}`)
                                      .then(() => setSiteOverrideData((prev) => {
                                        if (!prev) return prev;
                                        const updated = { ...prev.roleOverrides };
                                        delete updated[role];
                                        return { ...prev, roleOverrides: updated };
                                      }))
                                      .catch(() => alert("Delete failed."))
                                      .finally(() => setSiteOverrideSaving(null));
                                  }}
                                  className="px-2 py-1 rounded bg-red-900/40 hover:bg-red-800/60 text-red-300 text-xs disabled:opacity-40"
                                >
                                  {siteOverrideSaving === role ? "…" : "Remove"}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Add new role override */}
                      <div className="mt-3 pt-3 border-t border-slate-700/50">
                        <p className="text-xs text-slate-500 mb-2">Add a role override:</p>
                        <AddRoleOverride
                          siteId={siteOverrideSiteId}
                          availableModels={(platformSettings.availableModels ?? []).filter((m) => !m.isInternal)}
                          onSaved={(role, model) => {
                            setSiteOverrideData((prev) => prev ? {
                              ...prev,
                              roleOverrides: { ...prev.roleOverrides, [role]: model }
                            } : prev);
                          }}
                        />
                      </div>
                    </div>

                    {/* Customer API Credentials */}
                    <div className="border border-amber-700/40 rounded-lg p-4 bg-amber-950/10">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Customer API Credentials</div>
                        {siteCredentials?.hasApiKeyOverride && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-900/40 text-amber-300 border border-amber-700/50">
                            Custom key active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mb-4">
                        For government or enterprise clients with their own OpenAI account.
                        Their traffic will route to their key instead of Velaro's shared resource.
                        The key is never shown after saving.
                      </p>

                      {siteCredentials?.hasApiKeyOverride && (
                        <div className="flex items-center gap-3 mb-4 p-3 rounded bg-amber-900/20 border border-amber-700/30">
                          <i className="fa fa-key text-amber-400 text-xs" />
                          <span className="text-xs text-amber-300">Custom API key is set. Enter a new key to replace it, or clear to revert to Velaro shared.</span>
                          <button
                            type="button"
                            disabled={credentialsClearing}
                            onClick={() => {
                              if (!confirm(`Clear API key for site ${siteOverrideSiteId}? They will revert to Velaro's shared AI resource.`)) return;
                              setCredentialsClearing(true);
                              api.admin.post(`Copilot/SiteAiCredentials/${siteOverrideSiteId}`, { apiKey: "", endpoint: "" })
                                .then((r) => r.json())
                                .then((d) => { setSiteCredentials(d); setNewApiKey(""); setNewEndpoint(""); })
                                .catch(() => alert("Clear failed."))
                                .finally(() => setCredentialsClearing(false));
                            }}
                            className="ml-auto px-3 py-1 rounded text-xs text-red-400 border border-red-800/50 hover:bg-red-900/20 disabled:opacity-40"
                          >
                            {credentialsClearing ? "Clearing…" : "Clear — use Velaro shared"}
                          </button>
                        </div>
                      )}

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">
                            API Key {siteCredentials?.hasApiKeyOverride ? "(leave blank to keep existing)" : ""}
                          </label>
                          <input
                            type="password"
                            value={newApiKey}
                            onChange={(e) => setNewApiKey(e.target.value)}
                            placeholder={siteCredentials?.hasApiKeyOverride ? "••••••••• (set — enter new to replace)" : "sk-proj-... or Azure key"}
                            className="w-full bg-slate-900 border border-slate-600 rounded text-white text-xs px-3 py-1.5 font-mono placeholder-slate-600"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">
                            Azure Endpoint <span className="text-slate-600 font-normal">(optional — leave blank for direct api.openai.com)</span>
                          </label>
                          <input
                            type="text"
                            value={newEndpoint}
                            onChange={(e) => setNewEndpoint(e.target.value)}
                            placeholder="https://customer-name.openai.azure.com/"
                            className="w-full bg-slate-900 border border-slate-600 rounded text-white text-xs px-3 py-1.5 font-mono placeholder-slate-600"
                          />
                          <p className="text-xs text-slate-600 mt-1">
                            With endpoint: key routes to their Azure account (they keep data in their Azure tenant).
                            Without endpoint: key routes to direct api.openai.com.
                          </p>
                        </div>
                        <div className="flex items-center gap-3 pt-1">
                          <button
                            type="button"
                            disabled={credentialsSaving || (!newApiKey && !newEndpoint)}
                            onClick={() => {
                              setCredentialsSaving(true);
                              setCredentialsSaved(false);
                              const payload: Record<string, string> = {};
                              if (newApiKey) payload.apiKey = newApiKey;
                              if (newEndpoint !== (siteCredentials?.endpointOverride ?? "")) payload.endpoint = newEndpoint;
                              api.admin.post(`Copilot/SiteAiCredentials/${siteOverrideSiteId}`, payload)
                                .then((r) => r.json())
                                .then((d) => { setSiteCredentials(d); setNewApiKey(""); setCredentialsSaved(true); setTimeout(() => setCredentialsSaved(false), 4000); })
                                .catch(() => alert("Save failed."))
                                .finally(() => setCredentialsSaving(false));
                            }}
                            className="px-4 py-1.5 rounded bg-amber-700 hover:bg-amber-600 text-white text-xs font-medium disabled:opacity-40 transition-colors"
                          >
                            {credentialsSaving ? "Saving…" : "Save Credentials"}
                          </button>
                          {credentialsSaved && <span className="text-green-400 text-xs">Saved — key stored, never shown again.</span>}
                        </div>
                      </div>
                    </div>

                    {/* Effective Models — what actually fires for this site */}
                    {effectiveModels !== null && (
                      <div className="border border-emerald-800/40 rounded-lg p-4 bg-emerald-950/10">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="text-xs font-semibold text-emerald-400 uppercase tracking-wide">What's Actually Firing</div>
                          <span className="text-[10px] text-slate-500">Resolved model per role after all overrides applied</span>
                        </div>
                        <div className="grid grid-cols-1 gap-1.5">
                          {effectiveModels.map((row) => {
                            const roleInfo = AI_ROLES.find((r) => r.role === row.role);
                            const modelInfo = (platformSettings?.availableModels ?? []).find(
                              (m) => m.alias === row.model || m.apiModelId === row.model
                            );
                            const modelLabel = modelInfo ? (modelInfo.brandedName || modelInfo.displayName) : row.model;
                            return (
                              <div key={row.role} className="flex items-center gap-3 py-1.5 border-b border-slate-700/30 last:border-0">
                                <div className="w-44 flex-shrink-0">
                                  <span className="text-xs text-slate-300 font-medium">{roleInfo?.label ?? row.role}</span>
                                </div>
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <span className={`text-xs font-mono truncate ${row.isOverridden ? "text-amber-300" : "text-slate-400"}`}>{modelLabel}</span>
                                  {row.isOverridden && (
                                    <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300 border border-amber-700/40">overridden</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Recent AI Usage */}
                    {siteUsage !== null && (
                      <div className="border border-slate-700 rounded-lg p-4">
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Recent Model Usage (last 20 calls)</div>
                        {siteUsage.length === 0 ? (
                          <p className="text-xs text-slate-500">No usage data for this site yet.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-slate-700 text-slate-500">
                                  <th className="text-left pb-2 pr-4">Role</th>
                                  <th className="text-left pb-2 pr-4">Model</th>
                                  <th className="text-right pb-2">Tokens In / Out</th>
                                </tr>
                              </thead>
                              <tbody>
                                {siteUsage.map((row, i) => {
                                  const modelInfo = (platformSettings.availableModels ?? []).find(
                                    (m) => m.apiModelId === row.modelId || m.alias === row.modelId
                                  );
                                  const modelLabel = modelInfo
                                    ? (modelInfo.brandedName || modelInfo.displayName)
                                    : row.modelId;
                                  return (
                                    <tr key={i} className="border-b border-slate-700/50 last:border-0">
                                      <td className="py-1.5 pr-4 text-slate-300">{row.role}</td>
                                      <td className="py-1.5 pr-4 text-slate-400">{modelLabel}</td>
                                      <td className="py-1.5 text-right font-mono text-slate-400">
                                        {row.inputTokens.toLocaleString()} / {row.outputTokens.toLocaleString()}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Provider API Keys ────────────────────────────────────── */}
              <div className="bg-slate-800 border border-amber-600/40 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-1">Provider API Keys</h3>
                <p className="text-slate-400 text-xs mb-4">
                  Keys required to enable non-Anthropic/non-Azure models. Saved to the platform DB —
                  no Azure Portal access needed. Keys are write-only after saving (masked display only).
                </p>
                {!providerKeys ? (
                  <div className="text-slate-400 text-xs">Loading…</div>
                ) : (
                  <div className="space-y-4">
                    {[
                      {
                        provider: "qwen",
                        label: "Qwen (Alibaba DashScope)",
                        models: "velaro-qwen-2-5-72b",
                        url: "https://dashscope.console.aliyun.com/",
                        note: "⚠️ Data routes through Alibaba Cloud (China). Do not enable for HIPAA or regulated customers.",
                        placeholder: "sk-…",
                      },
                      {
                        provider: "google",
                        label: "Google Gemini",
                        models: "velaro-gemini-2.0-flash, velaro-gemini-1.5-pro",
                        url: "https://aistudio.google.com/app/apikey",
                        note: "Google AI Studio API key. Used for all Gemini model calls.",
                        placeholder: "AIza…",
                      },
                    ].map(({ provider, label, models, url, note, placeholder }) => {
                      const row = providerKeys.find((k) => k.provider === provider);
                      const isConfigured = row?.configured ?? false;
                      const inputVal = providerKeyInput[provider] ?? "";
                      const saving = providerKeySaving === provider;
                      return (
                        <div key={provider} className={`border rounded-lg p-4 ${isConfigured ? "border-emerald-700/50 bg-emerald-950/10" : "border-slate-700"}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isConfigured ? "bg-emerald-400" : "bg-slate-600"}`} />
                            <span className="text-sm text-white font-medium">{label}</span>
                            {isConfigured && (
                              <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-900/40 text-emerald-300 border border-emerald-700/50">
                                Configured
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mb-0.5 ml-4">Models: <code className="text-slate-400">{models}</code></p>
                          {note && <p className="text-xs text-amber-500/80 mb-2 ml-4">{note}</p>}
                          {isConfigured && row?.maskedValue && (
                            <div className="flex items-center gap-2 mb-2 ml-4">
                              <code className="text-xs text-slate-400 bg-slate-900/50 px-2 py-0.5 rounded">{row.maskedValue}</code>
                              {row.updatedBy && <span className="text-xs text-slate-600">set by {row.updatedBy}</span>}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <input
                              type="password"
                              value={inputVal}
                              onChange={(e) => setProviderKeyInput((prev) => ({ ...prev, [provider]: e.target.value }))}
                              placeholder={isConfigured ? "Enter new key to replace…" : placeholder}
                              className="flex-1 bg-slate-900 border border-slate-600 rounded text-white text-xs px-3 py-1.5 font-mono"
                            />
                            <button
                              type="button"
                              disabled={!inputVal.trim() || saving}
                              onClick={() => {
                                setProviderKeySaving(provider);
                                api.messaging.post(`superadmin/ai-models/provider-keys/${provider}`, { apiKey: inputVal.trim() })
                                  .then((r) => r.json())
                                  .then((d) => {
                                    setProviderKeys((prev) => prev
                                      ? prev.map((k) => k.provider === provider ? { ...k, configured: d.configured, maskedValue: d.maskedValue } : k)
                                      : prev
                                    );
                                    setProviderKeyInput((prev) => ({ ...prev, [provider]: "" }));
                                  })
                                  .catch(() => alert(`Failed to save ${label} key.`))
                                  .finally(() => setProviderKeySaving(null));
                              }}
                              className="px-3 py-1.5 rounded bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-xs font-medium"
                            >
                              {saving ? "Saving…" : isConfigured ? "Replace" : "Save"}
                            </button>
                            {isConfigured && (
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => {
                                  if (!confirm(`Clear ${label} API key? Models using this provider will fail until a new key is set.`)) return;
                                  setProviderKeySaving(provider);
                                  api.messaging.post(`superadmin/ai-models/provider-keys/${provider}`, { apiKey: null })
                                    .then(() => {
                                      setProviderKeys((prev) => prev
                                        ? prev.map((k) => k.provider === provider ? { ...k, configured: false, maskedValue: null } : k)
                                        : prev
                                      );
                                    })
                                    .catch(() => alert(`Failed to clear ${label} key.`))
                                    .finally(() => setProviderKeySaving(null));
                                }}
                                className="px-3 py-1.5 rounded bg-red-900/40 hover:bg-red-800/60 text-red-300 text-xs"
                              >
                                Clear
                              </button>
                            )}
                            <a href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-400 hover:underline flex-shrink-0">
                              Get key
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Model Cost Reference ─────────────────────────────────── */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-1">Model Cost Reference</h3>
                <p className="text-slate-400 text-xs mb-4">
                  Per 1M tokens — typical chatbot call is ~2–5K tokens in + ~200–500 tokens out.
                  At 10K conversations/month × 3K avg tokens: Haiku ≈ $2.40/mo · GPT-5 mini ≈ $1.20/mo · GPT-5 ≈ $7.50/mo.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-700 text-slate-500">
                        <th className="text-left pb-2 pr-4 font-medium">Model</th>
                        <th className="text-left pb-2 pr-4 font-medium">Provider</th>
                        <th className="text-right pb-2 pr-4 font-medium">Input / 1M</th>
                        <th className="text-right pb-2 pr-4 font-medium">Output / 1M</th>
                        <th className="text-right pb-2 pr-4 font-medium">Cost tier</th>
                        <th className="text-left pb-2 font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { alias: "velaro-gpt-5-mini",         label: "GPT-5 mini",    provider: "Azure/OpenAI", note: "Cheapest overall. No Anthropic." },
                        { alias: "velaro-gpt-4o-mini",        label: "GPT-4o mini",   provider: "Azure/OpenAI", note: "Legacy cheap OpenAI (auto-upgrades to GPT-5)." },
                        { alias: "claude-haiku-4-5-20251001", label: "Haiku 4.5",     provider: "Anthropic",    note: "Solid baseline. Standard default." },
                        { alias: "velaro-gpt-5",              label: "GPT-5",         provider: "Azure/OpenAI", note: "~3× Haiku cost. No Anthropic. Enterprise." },
                        { alias: "velaro-gpt-4o",             label: "GPT-4o",        provider: "Azure/OpenAI", note: "Legacy (auto-upgrades to GPT-5)." },
                        { alias: "claude-sonnet-4-6",         label: "Sonnet 4.6",    provider: "Anthropic",    note: "Best Anthropic mid-tier." },
                        { alias: "claude-opus-4-6",           label: "Opus 4.6",      provider: "Anthropic",    note: "Top-tier. Reserve for high-value flows." },
                      ].map((row) => {
                        const c = MODEL_COSTS[row.alias];
                        const cl = costLabel(row.alias);
                        const clColor = !cl ? "" : cl === "¢" ? "text-emerald-400" : cl === "$$" ? "text-yellow-400" : cl === "$$$" ? "text-orange-400" : "text-red-400";
                        return (
                          <tr key={row.alias} className="border-b border-slate-700/40 last:border-0">
                            <td className="py-2 pr-4 text-white font-medium">{row.label}</td>
                            <td className="py-2 pr-4 text-slate-400">{row.provider}</td>
                            <td className="py-2 pr-4 text-right font-mono text-slate-300">{c ? `$${c.input.toFixed(2)}` : "—"}</td>
                            <td className="py-2 pr-4 text-right font-mono text-slate-300">{c ? `$${c.output.toFixed(2)}` : "—"}</td>
                            <td className={`py-2 pr-4 text-right font-bold ${clColor}`}>{cl ?? "—"}</td>
                            <td className="py-2 text-slate-500">{row.note}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Customer Model Overrides ──────────────────────────────── */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-white font-semibold">Customer Model Overrides</h3>
                  <button
                    type="button"
                    onClick={() => { setAllSiteOverrides(null); }}
                    className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    title="Refresh list"
                  >
                    <i className="fa fa-refresh mr-1" />Refresh
                  </button>
                </div>
                <p className="text-slate-400 text-xs mb-4">
                  Every customer who has a role-level model override set. Click a row to change or remove it.
                  Customers not listed here use the platform defaults above.
                </p>

                {/* Role filter */}
                <div className="flex items-center gap-3 mb-4">
                  <label className="text-xs text-slate-500">Filter by role:</label>
                  <select
                    value={overrideFilterRole}
                    onChange={(e) => setOverrideFilterRole(e.target.value)}
                    className="bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5"
                  >
                    <option value="">All roles</option>
                    {AI_ROLES.map((r) => <option key={r.role} value={r.role}>{r.label}</option>)}
                  </select>
                  {allSiteOverrides && (
                    <span className="text-xs text-slate-500">
                      {(overrideFilterRole
                        ? allSiteOverrides.filter((o) => o.role === overrideFilterRole)
                        : allSiteOverrides
                      ).length} overrides
                    </span>
                  )}
                </div>

                {allOverridesLoading ? (
                  <div className="text-slate-400 text-xs">Loading customer overrides…</div>
                ) : !allSiteOverrides || allSiteOverrides.length === 0 ? (
                  <div className="text-slate-500 text-xs py-4 text-center border border-dashed border-slate-700 rounded-lg">
                    No customer overrides set — all customers use platform defaults.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-700 text-slate-500">
                          <th className="text-left pb-2 pr-4 font-medium">Role</th>
                          <th className="text-left pb-2 pr-4 font-medium">Site ID</th>
                          <th className="text-left pb-2 pr-4 font-medium">Model</th>
                          <th className="text-right pb-2 pr-4 font-medium">Cost</th>
                          <th className="text-left pb-2 pr-4 font-medium">Updated</th>
                          <th className="text-left pb-2 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(overrideFilterRole
                          ? allSiteOverrides.filter((o) => o.role === overrideFilterRole)
                          : allSiteOverrides
                        ).map((row, i) => {
                          const roleInfo = AI_ROLES.find((r) => r.role === row.role);
                          // Resolve through DB alias overrides — if velaro-gpt-4o is remapped to velaro-gpt-5,
                          // show GPT-5 as the effective model rather than the raw stored alias.
                          const aliasRedirect = aliasOverrides?.find((a) => a.from.toLowerCase() === row.model.toLowerCase());
                          const effectiveModel = aliasRedirect ? aliasRedirect.to : row.model;
                          const modelInfo = (platformSettings?.availableModels ?? []).find(
                            (m) => m.alias === effectiveModel || m.apiModelId === effectiveModel
                          );
                          const modelLabel = modelInfo ? (modelInfo.brandedName || modelInfo.displayName) : effectiveModel;
                          const cl = costLabel(effectiveModel);
                          const clColor = !cl ? "text-slate-500" : cl === "¢" ? "text-emerald-400" : cl === "$$" ? "text-yellow-400" : cl === "$$$" ? "text-orange-400" : "text-red-400";
                          const isEditing = overrideEditSiteId === String(row.siteId) && overrideEditRole === row.role;

                          return (
                            <tr key={i} className={`border-b border-slate-700/40 last:border-0 ${isEditing ? "bg-slate-700/40" : "hover:bg-slate-700/20"}`}>
                              <td className="py-2 pr-4 text-slate-300 font-medium">{roleInfo?.label ?? row.role}</td>
                              <td className="py-2 pr-4 font-mono text-slate-400">{row.siteId}</td>
                              <td className="py-2 pr-4">
                                {isEditing ? (
                                  <select
                                    value={overrideEditModel}
                                    onChange={(e) => setOverrideEditModel(e.target.value)}
                                    className="bg-slate-900 border border-blue-500 rounded text-white text-xs px-2 py-1"
                                    autoFocus
                                  >
                                    {(platformSettings?.availableModels ?? []).filter((m) => !m.isInternal).map((m) => (
                                      <option key={m.alias} value={m.alias}>{m.brandedName || m.displayName} ({m.provider})</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="text-slate-300">
                                    {modelLabel}
                                    {aliasRedirect && (
                                      <span className="ml-1.5 text-xs text-slate-500" title={`Stored as: ${row.model}`}>
                                        ↑ <span className="font-mono">{row.model}</span>
                                      </span>
                                    )}
                                  </span>
                                )}
                              </td>
                              <td className={`py-2 pr-4 text-right font-bold ${clColor}`} title={costTooltip(effectiveModel)}>{cl ?? "—"}</td>
                              <td className="py-2 pr-4 text-slate-500">{row.updatedBy ?? "—"}</td>
                              <td className="py-2">
                                {isEditing ? (
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      disabled={overrideEditSaving}
                                      onClick={() => {
                                        setOverrideEditSaving(true);
                                        api.messaging.post(`superadmin/ai-models/site/${row.siteId}`, { role: row.role, model: overrideEditModel })
                                          .then(() => {
                                            setAllSiteOverrides((prev) => prev
                                              ? prev.map((o) => o.siteId === row.siteId && o.role === row.role ? { ...o, model: overrideEditModel } : o)
                                              : prev
                                            );
                                            setOverrideEditSiteId(null);
                                            setOverrideEditRole(null);
                                          })
                                          .catch(() => alert("Save failed."))
                                          .finally(() => setOverrideEditSaving(false));
                                      }}
                                      className="px-2 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-xs disabled:opacity-40"
                                    >
                                      {overrideEditSaving ? "…" : "Save"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => { setOverrideEditSiteId(null); setOverrideEditRole(null); }}
                                      className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white text-xs"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOverrideEditSiteId(String(row.siteId));
                                        setOverrideEditRole(row.role);
                                        setOverrideEditModel(row.model);
                                      }}
                                      className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white text-xs"
                                    >
                                      Change
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (!confirm(`Remove model override for site ${row.siteId} / ${row.role}? They'll revert to the platform default.`)) return;
                                        api.messaging.delete(`superadmin/ai-models/site/${row.siteId}/${row.role}`)
                                          .then(() => setAllSiteOverrides((prev) =>
                                            prev ? prev.filter((o) => !(o.siteId === row.siteId && o.role === row.role)) : prev
                                          ))
                                          .catch(() => alert("Remove failed."));
                                      }}
                                      className="px-2 py-1 rounded bg-red-900/40 hover:bg-red-800/60 text-red-300 text-xs"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Add override for any site */}
                <div className="mt-4 pt-4 border-t border-slate-700/50">
                  <p className="text-xs text-slate-500 mb-2">Add override for a site:</p>
                  <AddRoleOverride
                    siteId={siteOverrideSiteId || "0"}
                    availableModels={(platformSettings?.availableModels ?? []).filter((m) => !m.isInternal)}
                    onSaved={(role, model) => {
                      const newSiteId = parseInt(siteOverrideSiteId || "0", 10);
                      if (!newSiteId) return;
                      setAllSiteOverrides((prev) => {
                        if (!prev) return prev;
                        const filtered = prev.filter((o) => !(o.siteId === newSiteId && o.role === role));
                        return [...filtered, { siteId: newSiteId, role, model, updatedBy: "superadmin", updatedAt: new Date().toISOString() }];
                      });
                    }}
                    siteIdInput
                  />
                </div>
              </div>

              {/* Model alias registry */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-1">Model Alias Registry</h3>
                <p className="text-slate-400 text-xs mb-3">
                  Read-only view. To retire a model or change what an alias resolves to,
                  update <code className="text-slate-300">ModelAliasRegistry.cs</code> in velaro-admin
                  and <code className="text-slate-300">ModelResolver.cs</code> in velaro-messaging. No DB migration needed.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-700 text-slate-400">
                        <th className="text-left pb-2 pr-4">Alias stored in DB</th>
                        <th className="text-left pb-2 pr-4">Provider</th>
                        <th className="text-left pb-2 pr-4">Display Name</th>
                        <th className="text-left pb-2 pr-4">Tier gate</th>
                        <th className="text-left pb-2">Actual API model ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(platformSettings.availableModels ?? []).map((m) => {
                        const tierBadge: Record<string, string> = {
                          basic: "bg-slate-700 text-slate-300",
                          pro: "bg-blue-900/50 text-blue-300",
                          enterprise: "bg-purple-900/50 text-purple-300",
                        };
                        return (
                          <tr key={m.alias} className="border-b border-slate-700/50">
                            <td className="py-1.5 pr-4 font-mono text-slate-300">{m.alias}</td>
                            <td className="py-1.5 pr-4 text-slate-400">{m.provider}</td>
                            <td className="py-1.5 pr-4 text-slate-300">{m.displayName}</td>
                            <td className="py-1.5 pr-4">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${tierBadge[m.tier] ?? ""}`}>
                                {m.tier}
                              </span>
                            </td>
                            <td className="py-1.5 font-mono text-slate-400 text-xs">{m.apiModelId}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Model Routing (Alias Overrides) ─────────────────────────── */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-1">Model Routing Overrides</h3>
                <p className="text-slate-400 text-xs mb-1">
                  Remap a model alias at runtime — no code deploy needed.
                  If <code className="text-slate-300">velaro-gpt-4o</code> gets retired by Azure, add an override here
                  to route it to <code className="text-slate-300">velaro-gpt-5</code>.
                  All customers with <code className="text-slate-300">velaro-gpt-4o</code> on their account start getting the new model immediately.
                </p>
                <p className="text-amber-400/80 text-xs mb-4 flex items-center gap-1">
                  <i className="fa fa-info-circle" />
                  For Azure Foundry models: the target deployment must exist in Azure AI Foundry before this takes effect.
                  DevOps: Azure Portal → AI Foundry workspace → Deployments → New deployment.
                </p>

                {/* Existing overrides */}
                {aliasOverridesLoading ? (
                  <div className="text-slate-400 text-xs mb-4">Loading overrides…</div>
                ) : !aliasOverrides || aliasOverrides.length === 0 ? (
                  <div className="text-slate-500 text-xs py-3 text-center border border-dashed border-slate-700 rounded-lg mb-4">
                    No alias overrides configured — all aliases use the code-level defaults.
                  </div>
                ) : (
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-700 text-slate-500">
                          <th className="text-left pb-2 pr-4 font-medium">From alias</th>
                          <th className="text-left pb-2 pr-4 font-medium">Routes to</th>
                          <th className="text-left pb-2 pr-4 font-medium">Updated by</th>
                          <th className="text-left pb-2 pr-4 font-medium">Updated at</th>
                          <th className="text-left pb-2 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(aliasOverrides ?? []).map((row) => (
                          <tr key={row.from} className="border-b border-slate-700/40 last:border-0">
                            <td className="py-2 pr-4 font-mono text-slate-300">{row.from}</td>
                            <td className="py-2 pr-4 font-mono text-emerald-400">{row.to}</td>
                            <td className="py-2 pr-4 text-slate-500">{row.updatedBy ?? "—"}</td>
                            <td className="py-2 pr-4 text-slate-500">{row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : "—"}</td>
                            <td className="py-2">
                              <button
                                type="button"
                                disabled={aliasDeleting === row.from}
                                onClick={async () => {
                                  if (!confirm(`Remove alias override: ${row.from} → ${row.to}?`)) return;
                                  setAliasDeleting(row.from);
                                  try {
                                    await api.messaging.delete(`superadmin/ai-models/alias-overrides/${encodeURIComponent(row.from)}`);
                                    setAliasOverrides(null); // trigger reload
                                  } catch { alert("Delete failed."); }
                                  finally { setAliasDeleting(null); }
                                }}
                                className="text-xs text-red-400 hover:text-red-300 disabled:opacity-40"
                              >
                                {aliasDeleting === row.from ? "…" : "Delete"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Add new override */}
                <div className="border-t border-slate-700 pt-4">
                  <p className="text-slate-400 text-xs font-medium mb-2">Add new override</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      value={newAliasFrom}
                      onChange={(e) => setNewAliasFrom(e.target.value)}
                      placeholder="From alias (e.g. velaro-gpt-4o)"
                      className="flex-1 min-w-40 bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5 font-mono"
                    />
                    <span className="text-slate-500 text-xs">→</span>
                    <input
                      type="text"
                      value={newAliasTo}
                      onChange={(e) => setNewAliasTo(e.target.value)}
                      placeholder="To alias (e.g. velaro-gpt-5)"
                      className="flex-1 min-w-40 bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5 font-mono"
                    />
                    <button
                      type="button"
                      disabled={aliasSaving || !newAliasFrom.trim() || !newAliasTo.trim()}
                      onClick={async () => {
                        setAliasSaving(true);
                        try {
                          await api.messaging.post("superadmin/ai-models/alias-overrides", { from: newAliasFrom.trim(), to: newAliasTo.trim() });
                          setNewAliasFrom(""); setNewAliasTo("");
                          setAliasOverrides(null); // trigger reload
                        } catch { alert("Save failed."); }
                        finally { setAliasSaving(false); }
                      }}
                      className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-40 flex-shrink-0"
                    >
                      {aliasSaving ? "…" : "Add override"}
                    </button>
                  </div>
                </div>
              </div>

              {/* ── Custom OpenAI-Compatible Providers ──────────────────────────── */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-1">Custom Model Providers</h3>
                <p className="text-slate-400 text-xs mb-1">
                  Add any OpenAI-compatible API endpoint as a model. Works with Mistral, DeepSeek, Groq,
                  local deployments (LM Studio, vLLM), and others.
                  The model will appear in all dropdowns under the alias you choose.
                </p>
                <p className="text-amber-400/80 text-xs mb-4">
                  For Azure AI Foundry models: the deployment must be created in Azure first.
                  Contact DevOps to add a deployment, then register its alias here.
                </p>

                {/* Existing custom providers */}
                {customProvidersLoading ? (
                  <div className="text-slate-400 text-xs mb-4">Loading custom providers…</div>
                ) : !customProviders || customProviders.length === 0 ? (
                  <div className="text-slate-500 text-xs py-3 text-center border border-dashed border-slate-700 rounded-lg mb-4">
                    No custom providers configured.
                  </div>
                ) : (
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-700 text-slate-500">
                          <th className="text-left pb-2 pr-3 font-medium">Alias</th>
                          <th className="text-left pb-2 pr-3 font-medium">Display name</th>
                          <th className="text-left pb-2 pr-3 font-medium">Endpoint</th>
                          <th className="text-left pb-2 pr-3 font-medium">Model ID</th>
                          <th className="text-left pb-2 pr-3 font-medium">API key name</th>
                          <th className="text-left pb-2 pr-3 font-medium">Tier</th>
                          <th className="text-left pb-2 pr-3 font-medium">Updated</th>
                          <th className="text-left pb-2 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(customProviders ?? []).map((p) => (
                          <tr key={p.alias} className="border-b border-slate-700/40 last:border-0">
                            <td className="py-2 pr-3 font-mono text-slate-300">{p.alias}</td>
                            <td className="py-2 pr-3 text-white">{p.displayName}</td>
                            <td className="py-2 pr-3 font-mono text-slate-400 text-xs truncate max-w-40" title={p.endpoint}>{p.endpoint}</td>
                            <td className="py-2 pr-3 font-mono text-slate-400">{p.modelId}</td>
                            <td className="py-2 pr-3 text-slate-500">{p.providerKeyName ?? "—"}</td>
                            <td className="py-2 pr-3 text-slate-500">{p.tier ?? "—"}</td>
                            <td className="py-2 pr-3 text-slate-500">{p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : "—"}</td>
                            <td className="py-2">
                              <button
                                type="button"
                                disabled={customProviderDeleting === p.alias}
                                onClick={async () => {
                                  if (!confirm(`Remove custom provider: ${p.alias}?`)) return;
                                  setCustomProviderDeleting(p.alias);
                                  try {
                                    await api.messaging.delete(`superadmin/ai-models/custom-providers/${encodeURIComponent(p.alias)}`);
                                    setCustomProviders(null); // trigger reload
                                  } catch { alert("Delete failed."); }
                                  finally { setCustomProviderDeleting(null); }
                                }}
                                className="text-xs text-red-400 hover:text-red-300 disabled:opacity-40"
                              >
                                {customProviderDeleting === p.alias ? "…" : "Delete"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Add new custom provider form */}
                <div className="border-t border-slate-700 pt-4">
                  <p className="text-slate-400 text-xs font-medium mb-2">Add new provider</p>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      value={newCustomProvider.alias}
                      onChange={(e) => setNewCustomProvider((p) => ({ ...p, alias: e.target.value }))}
                      placeholder="Alias (e.g. mistral-large)"
                      className="bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5 font-mono"
                    />
                    <input
                      type="text"
                      value={newCustomProvider.displayName}
                      onChange={(e) => setNewCustomProvider((p) => ({ ...p, displayName: e.target.value }))}
                      placeholder="Display name (e.g. Mistral Large)"
                      className="bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5"
                    />
                    <input
                      type="text"
                      value={newCustomProvider.endpoint}
                      onChange={(e) => setNewCustomProvider((p) => ({ ...p, endpoint: e.target.value }))}
                      placeholder="Endpoint (e.g. https://api.mistral.ai/v1)"
                      className="bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5 font-mono col-span-2"
                    />
                    <input
                      type="text"
                      value={newCustomProvider.modelId}
                      onChange={(e) => setNewCustomProvider((p) => ({ ...p, modelId: e.target.value }))}
                      placeholder="Model ID sent to API (e.g. mistral-large-latest)"
                      className="bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5 font-mono"
                    />
                    <input
                      type="text"
                      value={newCustomProvider.providerKeyName}
                      onChange={(e) => setNewCustomProvider((p) => ({ ...p, providerKeyName: e.target.value }))}
                      placeholder="API key name (e.g. mistral — must match Provider API Keys)"
                      className="bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5"
                    />
                    <input
                      type="text"
                      value={newCustomProvider.tier}
                      onChange={(e) => setNewCustomProvider((p) => ({ ...p, tier: e.target.value }))}
                      placeholder="Tier (basic / pro / enterprise)"
                      className="bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5"
                    />
                    <input
                      type="text"
                      value={newCustomProvider.notes}
                      onChange={(e) => setNewCustomProvider((p) => ({ ...p, notes: e.target.value }))}
                      placeholder="Notes (optional)"
                      className="bg-slate-900 border border-slate-600 rounded text-white text-xs px-2 py-1.5"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={customProviderSaving || !newCustomProvider.alias.trim() || !newCustomProvider.endpoint.trim() || !newCustomProvider.modelId.trim()}
                    onClick={async () => {
                      setCustomProviderSaving(true);
                      try {
                        await api.messaging.post("superadmin/ai-models/custom-providers", {
                          alias:          newCustomProvider.alias.trim(),
                          displayName:    newCustomProvider.displayName.trim() || newCustomProvider.alias.trim(),
                          endpoint:       newCustomProvider.endpoint.trim(),
                          modelId:        newCustomProvider.modelId.trim(),
                          providerKeyName: newCustomProvider.providerKeyName.trim() || null,
                          tier:           newCustomProvider.tier.trim() || null,
                          notes:          newCustomProvider.notes.trim() || null,
                        });
                        setNewCustomProvider({ alias: "", displayName: "", endpoint: "", modelId: "", providerKeyName: "", tier: "", notes: "" });
                        setCustomProviders(null); // trigger reload
                      } catch { alert("Save failed."); }
                      finally { setCustomProviderSaving(false); }
                    }}
                    className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-40"
                  >
                    {customProviderSaving ? "…" : "Add provider"}
                  </button>
                </div>
              </div>

              {/* Provisioning-Required Models */}
              {(platformSettings.availableModels ?? []).some((m) => m.requiresProvisioning) && (
                <div className="bg-slate-800 border border-amber-700/50 rounded-xl p-5">
                  <h3 className="text-amber-300 font-semibold mb-1 flex items-center gap-2">
                    <span>⚙️</span> Provisioning-Required Models
                  </h3>
                  <p className="text-slate-400 text-xs mb-4">
                    These models are gated — customers on the right tier can request access, but Velaro must
                    manually provision the API key in App Settings before the model becomes usable.
                    When a customer requests access, follow the setup steps below.
                  </p>
                  {(platformSettings.availableModels ?? [])
                    .filter((m) => m.requiresProvisioning)
                    .map((m) => (
                      <div key={m.alias} className="border border-slate-700 rounded-lg p-4 mb-3 last:mb-0">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-white font-semibold">{m.displayName}</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-purple-900/50 text-purple-300">{m.tier}</span>
                          <span className="text-xs text-slate-400">{m.provider}</span>
                          <span className="font-mono text-xs text-slate-500">{m.alias}</span>
                        </div>

                        {m.alias === "velaro-qwen-2-5-72b" && (
                          <div className="space-y-3">
                            <div className="bg-amber-900/20 border border-amber-700/40 rounded p-3 text-xs text-amber-200">
                              <strong>⚠️ Compliance notice:</strong> Qwen is built by Alibaba Cloud.
                              All inference requests route through Alibaba's infrastructure in China.
                              Do NOT enable for HIPAA, FINRA, FedRAMP, or other regulated-industry customers.
                              Suitable for: general multilingual support, e-commerce, non-PII chatbot use cases.
                            </div>

                            <div>
                              <p className="text-slate-300 text-xs font-semibold mb-1">When to use Qwen 2.5 72B:</p>
                              <ul className="text-slate-400 text-xs list-disc ml-4 space-y-0.5">
                                <li>Customers needing strong Chinese / East Asian language support</li>
                                <li>Multilingual e-commerce bots (Chinese, Japanese, Korean, Arabic)</li>
                                <li>Cost-sensitive enterprise accounts that want a non-Azure model option</li>
                                <li>Accounts where GPT-4o is overkill but GPT-4o-mini isn't capable enough</li>
                              </ul>
                            </div>

                            <div>
                              <p className="text-slate-300 text-xs font-semibold mb-2">Setup steps (do this when a customer requests access):</p>
                              <ol className="text-slate-400 text-xs list-decimal ml-4 space-y-1 mb-3">
                                <li>Go to <a href="https://dashscope.console.aliyun.com/" target="_blank" rel="noreferrer" className="text-blue-400 underline">dashscope.console.aliyun.com</a> and sign in with the Velaro Alibaba Cloud account</li>
                                <li>Navigate to <strong className="text-slate-300">API Key Management</strong> → <strong className="text-slate-300">Create API Key</strong></li>
                                <li>Copy the key (starts with <code className="bg-slate-900 px-1 rounded">sk-</code>)</li>
                                <li>Set the <code className="bg-slate-900 px-1 rounded">QwenApiKey</code> App Setting on the customer's messaging App Service (or staging/prod below)</li>
                                <li>In Admin DB: set customer's bot <code className="bg-slate-900 px-1 rounded">AIModel = "velaro-qwen-2-5-72b"</code></li>
                              </ol>
                            </div>

                            <div>
                              <p className="text-slate-300 text-xs font-semibold mb-2">Azure CLI — set QwenApiKey:</p>
                              <div className="space-y-2">
                                <div>
                                  <p className="text-slate-500 text-xs mb-1">Staging:</p>
                                  <pre className="bg-slate-900 rounded p-3 text-xs text-green-300 overflow-x-auto whitespace-pre">{`az webapp config appsettings set \\
  --subscription bf6c4494-5e1e-434e-97bf-2a92954c80c9 \\
  --resource-group Default-Web-EastUS \\
  --name velaro-messaging-api-staging \\
  --settings QwenApiKey="sk-YOUR-KEY-HERE"`}</pre>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs mb-1">Production:</p>
                                  <pre className="bg-slate-900 rounded p-3 text-xs text-green-300 overflow-x-auto whitespace-pre">{`az webapp config appsettings set \\
  --subscription "v10 Production" \\
  --resource-group Default-Web-EastUS \\
  --name velaro-messaging-api-prod \\
  --settings QwenApiKey="sk-YOUR-KEY-HERE"`}</pre>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs mb-1">Verify the setting was saved:</p>
                                  <pre className="bg-slate-900 rounded p-3 text-xs text-green-300 overflow-x-auto whitespace-pre">{`az webapp config appsettings list \\
  --subscription bf6c4494-5e1e-434e-97bf-2a92954c80c9 \\
  --resource-group Default-Web-EastUS \\
  --name velaro-messaging-api-staging \\
  --query "[?name=='QwenApiKey']"`}</pre>
                                </div>
                              </div>
                            </div>

                            <div className="text-xs text-slate-500">
                              App Setting name: <code className="bg-slate-900 px-1 rounded text-slate-300">QwenApiKey</code> ·
                              Endpoint: <code className="bg-slate-900 px-1 rounded text-slate-300">https://dashscope.aliyuncs.com/compatible-mode/v1</code> ·
                              Model ID sent to API: <code className="bg-slate-900 px-1 rounded text-slate-300">qwen2.5-72b-instruct</code>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {/* Copilot system prompt header */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-white font-semibold">Copilot System Prompt Header</h3>
                  <button
                    type="button"
                    className="text-xs text-slate-400 hover:text-amber-300 transition-colors"
                    onClick={() => {
                      if (!confirm("Reset to the hardcoded default? This will clear any customizations.")) return;
                      api.admin.post("Copilot/PlatformAISettings/ResetPrompt", {})
                        .then((r) => r.json())
                        .then((d) => {
                          setPlatformSettings((s) => s ? { ...s, systemPromptHeader: d.defaultPrompt, isSystemPromptCustomized: false } : s);
                          setPlatformDirty(false);
                        })
                        .catch(() => alert("Reset failed."));
                    }}
                  >
                    Reset to default
                  </button>
                </div>
                <p className="text-slate-400 text-xs mb-3">
                  The static identity + rules injected at the top of every Copilot conversation.
                  The dynamic sections (tool definitions, subscription flags, navigation paths, KB docs) are
                  always appended from code and are not editable here.
                  Saved to DB — used by all customers. Falls back to the hardcoded default if DB is unavailable.
                </p>
                <textarea
                  className="w-full bg-slate-900 border border-slate-600 rounded text-slate-200 text-xs font-mono p-3 resize-y"
                  rows={14}
                  value={platformSettings.systemPromptHeader}
                  onChange={(e) => {
                    setPlatformSettings((s) => s ? { ...s, systemPromptHeader: e.target.value } : s);
                    setPlatformDirty(true);
                  }}
                />
              </div>

              {/* Save bar */}
              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-slate-500">
                  Changes apply to new conversations immediately after saving. Existing sessions are unaffected.
                </p>
                <button
                  type="button"
                  disabled={!platformDirty || platformSaving}
                  onClick={() => {
                    setPlatformSaving(true);
                    api.admin.post("Copilot/PlatformAISettings", {
                      systemPromptHeader:      platformSettings.systemPromptHeader,
                      modelBasic:              platformSettings.modelBasic,
                      modelPro:                platformSettings.modelPro,
                      modelEnterprise:         platformSettings.modelEnterprise,
                      roleDefaults:            roleOverrides,
                      chatbotModelDefault:     chatbotModelDefault,
                      chatbotModelBasic:       chatbotModelBasic,
                      chatbotModelPro:         chatbotModelPro,
                      chatbotModelEnterprise:  chatbotModelEnterprise,
                    })
                      .then(() => setPlatformDirty(false))
                      .catch(() => alert("Save failed."))
                      .finally(() => setPlatformSaving(false));
                  }}
                  className="px-5 py-2 rounded-lg text-sm font-semibold text-white transition-colors bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {platformSaving ? "Saving…" : "Save Platform Settings"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
